import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { Box, Collapse, Typography } from '@mui/material';
import * as echarts from 'echarts';
import ReactEcharts from 'echarts-for-react';
import RepeatIcon from '@mui/icons-material/Repeat';
import BoltIcon from '@mui/icons-material/Bolt';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SortIcon from '@mui/icons-material/Sort';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import BarChartIcon from '@mui/icons-material/BarChart';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import GroupsIcon from '@mui/icons-material/Groups';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import InsightsIcon from '@mui/icons-material/Insights';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneIcon from '@mui/icons-material/Tune';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import useQuery from '../../utils/useQuery';
import { formatCompactNumber } from '../../utils';
import { callBackbonePostApi } from '../../services/backboneApiService';
import { EXERCISE_API_NAME_TO_ID, EXERCISE_ID_TO_SOURCE_ID } from '../../config/exercises';

import MetaTags from '../../components/MetaTags';
import Loading from '../../components/Loading';
import Container from '../../components/Layout/Container';
import { Heading1, Heading2, Heading3, Heading4 } from '../../components/Heading';
import FooterMenu from '../../components/Footer/FooterMenu';

import TopBanner from '../Leaderboards/TopBanner';

// ─── API + polling ──────────────────────────────────────────────────────────
const DEMO_LEADERBOARD_ACTION = 'getDemoLeaderboard';
const ROUTE_PATH = '/demo-leaderboard';
const SUCCESS_STATUS = 200;
const POLL_INTERVAL_MS = 15000;
const SWR_KEY_PREFIX = 'demoLeaderboard';
const SWR_ERROR_RETRY_COUNT = 3;
const SWR_ERROR_RETRY_INTERVAL_MS = 5000;

// ─── Filter / view enums ────────────────────────────────────────────────────
const SORT_BY = Object.freeze({
  REP_COUNT: 0,
  TOTAL_DAMAGE: 1,
  HEAVIEST_REP_WEIGHT: 2,
});

const SORT_BY_OPTIONS = Object.freeze([
  {
    id: SORT_BY.REP_COUNT,
    title: 'Reps',
    metric: 'repCount',
    Icon: RepeatIcon,
    formatValue: (n) => formatCompactNumber(n, 0),
    formatAxis: (n) => formatCompactNumber(n, 0),
  },
  {
    id: SORT_BY.TOTAL_DAMAGE,
    title: 'Damage',
    metric: 'totalDamage',
    Icon: BoltIcon,
    formatValue: (n) => formatCompactNumber(n, 0),
    formatAxis: (n) => formatCompactNumber(n, 0),
  },
  {
    id: SORT_BY.HEAVIEST_REP_WEIGHT,
    title: 'Heaviest',
    metric: 'heaviestRepWeight',
    Icon: FitnessCenterIcon,
    formatValue: (n) => `${formatCompactNumber(n, 1)} lbs`,
    formatAxis: (n) => formatCompactNumber(n, 1),
  },
]);

const WINDOW_OPTIONS = Object.freeze([
  { id: 1, title: 'Today' },
  { id: 7, title: '7 Days' },
  { id: 30, title: '30 Days' },
  { id: 90, title: '90 Days' },
  { id: 365, title: 'Year' },
]);

const EXERCISE_FILTER_ALL = 'all';
const EXERCISE_FILTER_OPTIONS = Object.freeze([
  { id: EXERCISE_FILTER_ALL, title: 'All' },
  { id: EXERCISE_API_NAME_TO_ID.ChestPress, title: 'Chest Press' },
  { id: EXERCISE_API_NAME_TO_ID.StandingCableRow, title: 'Cable Row' },
  { id: EXERCISE_API_NAME_TO_ID.OverheadPress, title: 'Overhead' },
  { id: EXERCISE_API_NAME_TO_ID.StiffLegDeadlift, title: 'Stiff Leg' },
  { id: EXERCISE_API_NAME_TO_ID.CableSquat, title: 'Squat' },
  { id: EXERCISE_API_NAME_TO_ID.LatPulldown, title: 'Pulldown' },
  { id: EXERCISE_API_NAME_TO_ID.BicepCurl, title: 'Bicep Curl' },
  { id: EXERCISE_API_NAME_TO_ID.TricepPushDown, title: 'Tricep' },
]);

const DEFAULT_EXERCISE_FILTER = EXERCISE_FILTER_ALL;
const EXERCISE_FILTER_IDS = new Set(EXERCISE_FILTER_OPTIONS.map((option) => option.id));
const SOURCE_EXERCISE_ID_TO_FILTER_ID = Object.freeze(
  Object.entries(EXERCISE_ID_TO_SOURCE_ID).reduce((acc, [id, sourceId]) => {
    acc[String(sourceId)] = id;
    return acc;
  }, {})
);

const VIEW = Object.freeze({
  LIST: 0,
  BAR_VERTICAL: 1,
  BAR_HORIZONTAL: 2,
  EVENT_SPREAD: 3,
  DISTRIBUTION: 4,
  TV: 5,
});

const VIEW_OPTIONS = Object.freeze([
  { id: VIEW.LIST, title: 'List', Icon: ViewListIcon },
  { id: VIEW.TV, title: 'TV', Icon: ViewColumnIcon },
  { id: VIEW.BAR_VERTICAL, title: 'Bars', Icon: BarChartIcon },
  { id: VIEW.BAR_HORIZONTAL, title: 'Ranked', Icon: StackedBarChartIcon },
  { id: VIEW.EVENT_SPREAD, title: 'By Event', Icon: GroupsIcon },
  { id: VIEW.DISTRIBUTION, title: 'Spread', Icon: EqualizerIcon },
]);

const DEFAULT_CALENDAR_DAYS = 30;
const DEFAULT_SORT_BY = SORT_BY.REP_COUNT;
const DEFAULT_VIEW = VIEW.LIST;
const DEFAULT_LIMIT = 50;
const MIN_CALENDAR_DAYS = 1;
const MAX_CALENDAR_DAYS = 365;
const MIN_LIMIT = 1;
const MAX_LIMIT = 50;
const MIN_VIEW = VIEW.LIST;
const MAX_VIEW = VIEW.TV;

// ─── Visual / timing constants ──────────────────────────────────────────────
const BAR_TOP_N_VERTICAL = 15;
const BAR_TOP_N_HORIZONTAL = 20;
const DISTRIBUTION_BUCKET_COUNT = 12;
const EVENT_SPREAD_MAX_EVENTS = 10;
const NEW_ENTRY_HIGHLIGHT_MS = 2500;
const CHART_HEIGHT_PX = 480;
const CHART_HEIGHT_HORIZONTAL_PX = 560;

const FEATURE_ENTER_MS = 420;
const FEATURE_HOLD_MS = 10000;
const FEATURE_FLY_MS = 850;
const FEATURE_SETTLE_MS = 700;
const FEATURE_FADE_MS = 220;
const FEATURE_SCROLL_INTO_VIEW_DELAY_MS = 240;
const FEATURE_SMOOTH_SCROLL_MS = 950;
const FEATURE_REVEAL_HANDOFF_DELAY_MS = 0;
const FEATURE_OVERLAY_Z_INDEX = 1600;
const FEATURE_OVERLAY_MAX_WIDTH_PX = 880;
// The overlay width is `min(viewport-ratio, max-px)`. The ratio is shared
// between the CSS declaration and the runtime scale calc so the two never
// drift apart (e.g. if someone bumps one but not the other).
const FEATURE_OVERLAY_VIEWPORT_RATIO = 0.9;
const FEATURE_OVERLAY_WIDTH_CSS = `min(${FEATURE_OVERLAY_VIEWPORT_RATIO * 100}vw, ${FEATURE_OVERLAY_MAX_WIDTH_PX}px)`;
// Floor for the fly-state scale so a freak measurement (e.g. a near-zero
// rect from a row that was about to detach) can't collapse the card to
// invisible mid-animation. 0.1 is well below any realistic slot/viewport
// ratio and keeps the transition perceptible if something goes wrong.
const FEATURE_OVERLAY_MIN_OUTER_SCALE = 0.1;
const FEATURE_FLY_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const FEATURE_ENTER_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';
const FEATURE_OVERLAY_ENTER_ANIMATION_NAME = 'demoLeaderboardFeatureOverlayEnter';
const FEATURE_NEW_ROW_ANIMATION_NAME = 'demoLeaderboardNewEntry';
const FEATURE_PULSE_ANIMATION_NAME = 'demoLeaderboardPulse';
const FEATURE_BACKDROP_Z_INDEX = 1599;
const FEATURE_BACKDROP_FADE_MS = 240;
const FEATURE_BACKDROP_ALPHA = 0.55;
const FEATURE_SCORE_COUNT_UP_MS = 2200;
const FEATURE_TROPHY_WIGGLE_ANIMATION_NAME = 'demoLeaderboardFeatureTrophyWiggle';
const FEATURE_TROPHY_WIGGLE_MS = 720;
const FEATURE_TROPHY_WIGGLE_DELAY_MS = 380;

const TOP_RANK = 1;
const PODIUM_RANK_CUTOFF = 3;
const PLACEHOLDER_INITIAL = '?';
const EM_DASH = '—';
const YYYY_MM_DD_LENGTH = 10;
const POLL_SECONDS_LABEL = `${Math.max(1, Math.round(POLL_INTERVAL_MS / 1000))}s`;
const PREFERS_REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const FILTERS_PANEL_DOM_ID = 'demo-leaderboard-filters-panel';

// ─── Color tokens ───────────────────────────────────────────────────────────
const PRIMARY_TEXT = '#FFFFFF';
const ACCENT_BLUE = '#0076FF';
const ACCENT_BLUE_LIGHT = '#7BB6FF';
const MUTED_TEXT = '#B1B1B1';
const SOFT_TEXT = '#8a8b8c';
const GOLD = '#F4CE02';
const GOLD_LIGHT = '#FFE36B';
const GOLD_DEEP = '#925F01';
const ROW_BG = '#1c1d1e';
const ROW_BG_HOVER = '#26282a';
const ROW_BORDER = '#2a2b2d';
const PLAIN_BORDER = '#3a3b3d';
const PANEL_BG = '#141516';
const TOP_ROW_BG = `linear-gradient(135deg, #2a1f08 0%, ${ROW_BG} 60%)`;
const LIVE_GREEN_DOT = '#4CAF50';
const LIVE_GREEN_TEXT = '#9DD49F';
const LIVE_INDICATOR_BG = 'rgba(76, 175, 80, 0.1)';
const LIVE_INDICATOR_BORDER = 'rgba(76, 175, 80, 0.3)';
const LEADERBOARD_MAX_WIDTH_PX = 900;
const CHART_MAX_WIDTH_PX = 1200;
// The TV view is sized for a wide horizontal display (typically a 16:9 TV
// running a big-screen dashboard). 1440px fits two ~640px columns plus
// gutter comfortably on a 1920px screen without feeling stretched.
const TV_MAX_WIDTH_PX = 1440;
const TV_COLUMN_LIMIT = 10;

const CHART_AXIS_TEXT = '#B1B1B1';
const CHART_AXIS_LINE = '#3a3b3d';
const CHART_TOOLTIP_BG = '#1a1b1c';

// ─── Low-level value helpers ────────────────────────────────────────────────
function clampInt(rawValue, min, max, fallback) {
  if (rawValue === null || rawValue === undefined || rawValue === '') return fallback;
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) return fallback;
  const integer = Math.trunc(parsed);
  if (integer < min) return min;
  if (integer > max) return max;
  return integer;
}

function isRecord(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function safeRankNumber(rank) {
  const n = Number(rank);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

function normalizeExerciseId(rawValue) {
  if (rawValue === null || rawValue === undefined) return null;
  let rawString;
  try {
    rawString = String(rawValue).trim();
  } catch {
    return null;
  }
  if (rawString.length === 0) return null;
  if (EXERCISE_FILTER_IDS.has(rawString) && rawString !== EXERCISE_FILTER_ALL) {
    return rawString;
  }
  if (EXERCISE_API_NAME_TO_ID[rawString]) {
    return EXERCISE_API_NAME_TO_ID[rawString];
  }
  const numeric = Number(rawString);
  if (Number.isInteger(numeric)) {
    return SOURCE_EXERCISE_ID_TO_FILTER_ID[String(numeric)] || null;
  }
  return null;
}

function normalizeExerciseFilter(rawValue) {
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return DEFAULT_EXERCISE_FILTER;
  }
  let rawString;
  try {
    rawString = String(rawValue).trim();
  } catch {
    return DEFAULT_EXERCISE_FILTER;
  }
  if (rawString === EXERCISE_FILTER_ALL) return EXERCISE_FILTER_ALL;
  return normalizeExerciseId(rawString) || DEFAULT_EXERCISE_FILTER;
}

function exerciseFilterToSourceId(exerciseFilter) {
  return EXERCISE_ID_TO_SOURCE_ID[exerciseFilter] || null;
}

function formatRankLabel(rank) {
  const n = safeRankNumber(rank);
  return n !== null ? `#${n}` : EM_DASH;
}

function hexToRgba(hex, alpha) {
  const fallback = `rgba(0, 0, 0, ${Number.isFinite(Number(alpha)) ? alpha : 1})`;
  if (typeof hex !== 'string') return fallback;
  const cleaned = hex.replace('#', '').trim();
  if (cleaned.length !== 6) return fallback;
  const r = parseInt(cleaned.slice(0, 2), 16);
  const g = parseInt(cleaned.slice(2, 4), 16);
  const b = parseInt(cleaned.slice(4, 6), 16);
  if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b)) return fallback;
  const a = Number.isFinite(Number(alpha)) ? alpha : 1;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

function goldRgba(alpha) {
  return hexToRgba(GOLD, alpha);
}

function accentBlueRgba(alpha) {
  return hexToRgba(ACCENT_BLUE, alpha);
}

// ─── Browser / DOM helpers ──────────────────────────────────────────────────
function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function formatDayKeyForDisplay(dayKey) {
  if (typeof dayKey !== 'string' || dayKey.length !== YYYY_MM_DD_LENGTH) return '';
  const [year, month, day] = dayKey.split('-').map(Number);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return '';
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function smoothScrollIntoView(node, options) {
  if (!node || typeof node.scrollIntoView !== 'function') return;
  try {
    node.scrollIntoView({ behavior: 'smooth', ...(options || {}) });
  } catch {
    try {
      node.scrollIntoView();
    } catch {
      // ignore; node may have been detached or scroll APIs unavailable
    }
  }
}

// Module-level rAF id so a new feature dequeueing mid-scroll can cancel the
// in-flight animation by starting its own. We never have more than one
// DemoLeaderboard mounted at a time, so module scope is safe here.
let activeSmoothScrollRafId = null;
function cancelActiveSmoothScroll() {
  if (
    activeSmoothScrollRafId !== null &&
    typeof window !== 'undefined' &&
    typeof window.cancelAnimationFrame === 'function'
  ) {
    window.cancelAnimationFrame(activeSmoothScrollRafId);
  }
  activeSmoothScrollRafId = null;
}

function readScrollY() {
  if (typeof window === 'undefined') return 0;
  const raw =
    typeof window.pageYOffset === 'number' ? window.pageYOffset : window.scrollY;
  return Number.isFinite(raw) ? raw : 0;
}

// Calls scrollIntoView in a way that intentionally bypasses any global
// `scroll-behavior: smooth` CSS so reduced-motion users actually get an
// instant jump. `behavior: 'instant'` is in the CSSOM spec and supported by
// modern browsers; we still try the no-arg form as a last resort.
function instantScrollIntoViewSafe(node) {
  if (!node || typeof node.scrollIntoView !== 'function') return;
  try {
    node.scrollIntoView({ block: 'center', behavior: 'instant' });
    return;
  } catch {
    // older browsers reject 'instant'; fall through
  }
  try {
    node.scrollIntoView({ block: 'center' });
  } catch {
    // ignore; node may have been detached or scroll APIs unavailable
  }
}

// Smoothly scrolls the page so `node` is vertically centered in the
// viewport. Native scrollIntoView({behavior:'smooth'}) is browser-tuned
// (usually too fast for a deliberate reveal); this hand-rolled version
// gives us a consistent ease-in-out cubic curve and a longer duration so
// the page glides into place behind the dimmed feature overlay.
//
// Robustness:
//   - Aborts if the user manually scrolls during the animation (so we never
//     fight their wheel/touch input).
//   - Aborts if the destination node detaches mid-flight so the page doesn't
//     come to rest on a stale Y coordinate.
//   - Guards against non-finite scrollY targets so a misbehaving host can't
//     throw inside window.scrollTo.
function smoothScrollPageToCenter(node, { durationMs, reduceMotion } = {}) {
  if (!node) return;
  if (typeof window === 'undefined') return;

  cancelActiveSmoothScroll();

  if (reduceMotion) {
    instantScrollIntoViewSafe(node);
    return;
  }

  if (
    typeof node.getBoundingClientRect !== 'function' ||
    typeof window.requestAnimationFrame !== 'function' ||
    typeof window.scrollTo !== 'function'
  ) {
    smoothScrollIntoView(node, { block: 'center' });
    return;
  }

  let rect;
  try {
    rect = node.getBoundingClientRect();
  } catch {
    return;
  }
  if (!rect || !Number.isFinite(rect.top) || !Number.isFinite(rect.height)) return;

  const viewportHeight =
    window.innerHeight ||
    (typeof document !== 'undefined' &&
      document.documentElement &&
      document.documentElement.clientHeight) ||
    0;
  const currentY = readScrollY();
  const targetY = currentY + rect.top + rect.height / 2 - viewportHeight / 2;
  const distance = targetY - currentY;

  if (
    !Number.isFinite(currentY) ||
    !Number.isFinite(targetY) ||
    !Number.isFinite(distance) ||
    Math.abs(distance) < 4
  ) {
    return;
  }

  const safeDuration =
    Number.isFinite(durationMs) && durationMs > 0 ? durationMs : FEATURE_SMOOTH_SCROLL_MS;

  let startTimestamp = null;
  let lastScheduledY = null;
  const USER_SCROLL_TOLERANCE_PX = 2;

  const step = (timestamp) => {
    // If the user scrolled (or anything else moved the page) since our last
    // frame, abandon the animation rather than fighting their input.
    if (lastScheduledY !== null) {
      const observedY = readScrollY();
      if (Math.abs(observedY - lastScheduledY) > USER_SCROLL_TOLERANCE_PX) {
        activeSmoothScrollRafId = null;
        return;
      }
    }
    // Bail if the destination row detached (poll filtered it out, route
    // change, etc.) so we don't come to rest on a stale coordinate.
    if (typeof node.isConnected === 'boolean' && !node.isConnected) {
      activeSmoothScrollRafId = null;
      return;
    }

    if (startTimestamp === null) startTimestamp = timestamp;
    const elapsed = timestamp - startTimestamp;
    const progress = Math.min(1, elapsed / safeDuration);
    // easeInOutCubic — gentle start, gentle end, no hard snap.
    const eased =
      progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;
    const nextY = currentY + distance * eased;
    if (Number.isFinite(nextY)) {
      window.scrollTo(0, nextY);
      lastScheduledY = nextY;
    }
    if (progress < 1) {
      activeSmoothScrollRafId = window.requestAnimationFrame(step);
    } else {
      activeSmoothScrollRafId = null;
    }
  };

  activeSmoothScrollRafId = window.requestAnimationFrame(step);
}

function measureRect(node) {
  if (!node || typeof node.getBoundingClientRect !== 'function') return null;
  let rect;
  try {
    rect = node.getBoundingClientRect();
  } catch {
    return null;
  }
  const top = Number(rect?.top);
  const left = Number(rect?.left);
  const width = Number(rect?.width);
  const height = Number(rect?.height);
  if (
    !Number.isFinite(top) ||
    !Number.isFinite(left) ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0
  ) {
    return null;
  }
  return { top, left, width, height };
}

function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    const mql = window.matchMedia(PREFERS_REDUCED_MOTION_QUERY);
    return Boolean(mql && mql.matches);
  } catch {
    return false;
  }
}

function clearTimeoutMap(map) {
  if (!map || typeof map.forEach !== 'function') return;
  map.forEach(clearTimeout);
  if (typeof map.clear === 'function') map.clear();
}

// ─── Keyboard ───────────────────────────────────────────────────────────────
function keydownIsActivation(event) {
  if (!event) return false;
  const key = event.key;
  return key === 'Enter' || key === ' ' || key === 'Spacebar';
}

function handleActivationKeyDown(event, onActivate) {
  if (!keydownIsActivation(event)) return;
  if (typeof event.preventDefault === 'function') event.preventDefault();
  if (typeof onActivate === 'function') onActivate();
}

// ─── Entry identity / extraction ────────────────────────────────────────────
function getEntryStableId(entry) {
  if (!isRecord(entry)) return null;
  if (entry.id !== null && entry.id !== undefined && entry.id !== '') {
    try {
      return String(entry.id);
    } catch {
      return null;
    }
  }
  if (entry.demoId !== null && entry.demoId !== undefined && entry.demoId !== '') {
    try {
      return String(entry.demoId);
    } catch {
      return null;
    }
  }
  const finalized = entry.finalizedAtMs;
  if (finalized === null || finalized === undefined) return null;
  let finalizedString;
  try {
    finalizedString = String(finalized);
  } catch {
    return null;
  }
  const first = typeof entry.firstName === 'string' ? entry.firstName : '';
  const last = typeof entry.lastName === 'string' ? entry.lastName : '';
  return `${finalizedString}|${first}|${last}`;
}

function getEntryReactKey(entry, fallbackIndex) {
  return getEntryStableId(entry) || `idx-${fallbackIndex}`;
}

function isEntryNew(entry, newIds) {
  if (!newIds || typeof newIds.has !== 'function') return false;
  const id = getEntryStableId(entry);
  return id !== null && newIds.has(id);
}

function getMetricNumber(entry, metric) {
  if (!isRecord(entry) || typeof metric !== 'string') return 0;
  const raw = entry[metric];
  const value = Number(raw);
  return Number.isFinite(value) ? value : 0;
}

// Strict whitelist applied at the entries source so every view (list, TV,
// chart) renders only rows where EVERY displayed field is meaningful. Any of:
//   - rank not a positive integer
//   - both firstName and lastName blank/whitespace
//   - active sort metric is not a finite positive number
// disqualifies the row. Without this, the list view rendered placeholder
// em-dashes for empty/zero-metric demos that shouldn't be on the board.
function hasDisplayableData(entry, metric) {
  if (!isRecord(entry)) return false;
  const rank = safeRankNumber(entry?.rank);
  if (rank === null || rank < 1) return false;
  const first = typeof entry.firstName === 'string' ? entry.firstName.trim() : '';
  const last = typeof entry.lastName === 'string' ? entry.lastName.trim() : '';
  if (first.length === 0 && last.length === 0) return false;
  const value = getMetricNumber(entry, metric);
  if (!Number.isFinite(value) || value <= 0) return false;
  return true;
}

function getOptionForSortBy(sortBy) {
  return SORT_BY_OPTIONS.find((option) => option.id === sortBy) || SORT_BY_OPTIONS[0];
}

function resolveSortOption(activeOption) {
  const option = activeOption || SORT_BY_OPTIONS[0];
  const Icon = option?.Icon || null;
  const formatValue =
    typeof option?.formatValue === 'function' ? option.formatValue : String;
  const formatAxis =
    typeof option?.formatAxis === 'function' ? option.formatAxis : String;
  const metric =
    typeof option?.metric === 'string' ? option.metric : SORT_BY_OPTIONS[0].metric;
  const title = typeof option?.title === 'string' ? option.title : '';
  return { option, Icon, formatValue, formatAxis, metric, title };
}

function getInitial(firstName) {
  if (typeof firstName !== 'string') return PLACEHOLDER_INITIAL;
  const trimmed = firstName.trim();
  return trimmed.length > 0 ? trimmed.slice(0, 1).toUpperCase() : PLACEHOLDER_INITIAL;
}

function paletteForName(firstName, lastName) {
  const seed = `${firstName || ''}${lastName || ''}`;
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  const index = Math.abs(hash) % MONOGRAM_PALETTES.length;
  return MONOGRAM_PALETTES[index];
}

function getDisplayName(entry) {
  const first = typeof entry?.firstName === 'string' ? entry.firstName.trim() : '';
  const last = typeof entry?.lastName === 'string' ? entry.lastName.trim() : '';
  const lastInitial = last.length > 0 ? `${last.slice(0, 1).toUpperCase()}.` : '';
  const composed = [first, lastInitial].filter(Boolean).join(' ');
  return composed || EM_DASH;
}

function getShortName(entry) {
  const first = typeof entry?.firstName === 'string' ? entry.firstName.trim() : '';
  if (first.length > 0) return first;
  const last = typeof entry?.lastName === 'string' ? entry.lastName.trim() : '';
  return last || EM_DASH;
}

// HTML-escape user-controlled strings before interpolating them into the
// ECharts tooltip `formatter` return value. ECharts assigns that string
// directly to the tooltip DOM (innerHTML-style), so an unescaped name like
// `<img src=x onerror=...>` would execute. Always run this on any value
// derived from `firstName`, `lastName`, `pageName`, or any other field that
// the backend can put arbitrary text into. Numeric formatters (formatValue,
// formatRankLabel) and our own constants don't need escaping.
function escapeHtml(value) {
  if (value === null || value === undefined) return '';
  const str = typeof value === 'string' ? value : String(value);
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function rowSizeForVariant(variant) {
  if (variant === 'top') return 'lg';
  if (variant === 'podium') return 'md';
  return 'sm';
}

// Featured rows render one tier larger than their natural list size so a new
// demo at any rank pops on the centered overlay. The shrink-back during the
// fly is handled by a CSS scale on the overlay card, not by re-rendering at
// the smaller tier, so the visual transition is smooth.
const SIZE_UP_ONE_TIER = Object.freeze({
  lg: 'xl',
  md: 'lg',
  sm: 'md',
});

function variantForRank(rank) {
  const n = safeRankNumber(rank);
  if (n === null) return 'standard';
  if (n === TOP_RANK) return 'top';
  if (n <= PODIUM_RANK_CUTOFF) return 'podium';
  return 'standard';
}

// ─── Arrival detection (shared) ─────────────────────────────────────────────
function diffEntryIds(entries, previousIds) {
  const currentIds = new Set();
  const arrivals = [];
  if (!Array.isArray(entries)) return { currentIds, arrivals };
  const previousHas =
    previousIds && typeof previousIds.has === 'function' ? previousIds : null;
  for (const entry of entries) {
    const id = getEntryStableId(entry);
    if (!id) continue;
    currentIds.add(id);
    if (previousHas && !previousHas.has(id)) arrivals.push(entry);
  }
  return { currentIds, arrivals };
}

function pickTopRankedArrival(arrivals) {
  if (!Array.isArray(arrivals) || arrivals.length === 0) return null;
  const candidates = arrivals.filter(isRecord);
  if (candidates.length === 0) return null;
  return candidates.reduce((best, current) => {
    const bestRank = safeRankNumber(best?.rank);
    const currentRank = safeRankNumber(current?.rank);
    const bestValue = bestRank !== null ? bestRank : Number.POSITIVE_INFINITY;
    const currentValue = currentRank !== null ? currentRank : Number.POSITIVE_INFINITY;
    return currentValue < bestValue ? current : best;
  });
}

function computePendingFeatureId({ entries, previousIds, enabled }) {
  if (!enabled) return null;
  if (!Array.isArray(entries) || entries.length === 0) return null;
  if (!previousIds || typeof previousIds.has !== 'function') return null;
  const { arrivals } = diffEntryIds(entries, previousIds);
  if (arrivals.length === 0) return null;
  const top = pickTopRankedArrival(arrivals);
  return top ? getEntryStableId(top) : null;
}

// ─── Hooks ──────────────────────────────────────────────────────────────────
function usePrefersReducedMotion() {
  const [matches, setMatches] = useState(() => prefersReducedMotion());
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    let mql;
    try {
      mql = window.matchMedia(PREFERS_REDUCED_MOTION_QUERY);
    } catch {
      return undefined;
    }
    if (!mql) return undefined;
    const handler = (event) => setMatches(Boolean(event?.matches));
    setMatches(Boolean(mql.matches));
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    if (typeof mql.addListener === 'function') {
      mql.addListener(handler);
      return () => mql.removeListener(handler);
    }
    return undefined;
  }, []);
  return matches;
}

/**
 * Manages a Set of ids where each id auto-expires after `ttlMs`.
 * Used by both the "new arrival highlight" and the "post-feature reveal" effects.
 */
function useTimedIdSet({ ttlMs, resetKey } = {}) {
  const [ids, setIds] = useState(() => new Set());
  const timersRef = useRef(new Map());

  useEffect(() => {
    const timers = timersRef.current;
    return () => clearTimeoutMap(timers);
  }, []);

  useEffect(() => {
    if (resetKey === undefined) return;
    clearTimeoutMap(timersRef.current);
    setIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, [resetKey]);

  const addOne = useCallback(
    (rawId) => {
      if (rawId === null || rawId === undefined || rawId === '') return;
      let key;
      try {
        key = String(rawId);
      } catch {
        return;
      }
      const ttl = Number.isFinite(Number(ttlMs)) && Number(ttlMs) > 0 ? Number(ttlMs) : 0;
      setIds((prev) => {
        if (prev.has(key)) return prev;
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      const timers = timersRef.current;
      const existing = timers.get(key);
      if (existing) clearTimeout(existing);
      if (ttl <= 0) return;
      timers.set(
        key,
        setTimeout(() => {
          timers.delete(key);
          setIds((prev) => {
            if (!prev.has(key)) return prev;
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        }, ttl)
      );
    },
    [ttlMs]
  );

  const addMany = useCallback(
    (rawIds) => {
      if (!Array.isArray(rawIds) && !(rawIds instanceof Set)) return;
      rawIds.forEach(addOne);
    },
    [addOne]
  );

  return { ids, add: addOne, addMany };
}

function useNewEntryIds(entries, ttlMs, dataVersionKey) {
  const previousIdsRef = useRef(null);
  const previousKeyRef = useRef(dataVersionKey);
  const { ids, addMany } = useTimedIdSet({ ttlMs, resetKey: dataVersionKey });

  const currentIds = useMemo(() => {
    const set = new Set();
    if (!Array.isArray(entries)) return set;
    entries.forEach((entry) => {
      const id = getEntryStableId(entry);
      if (id !== null) set.add(id);
    });
    return set;
  }, [entries]);

  useEffect(() => {
    // Reset baseline on data-version (filter/sort/window) changes so the
    // first poll for the new key isn't diffed against stale entries.
    if (previousKeyRef.current !== dataVersionKey) {
      previousKeyRef.current = dataVersionKey;
      previousIdsRef.current = null;
      return;
    }
    if (currentIds.size === 0) {
      previousIdsRef.current = null;
      return;
    }
    const previousIds = previousIdsRef.current;
    previousIdsRef.current = currentIds;
    if (previousIds === null) return;
    const arrived = [];
    currentIds.forEach((id) => {
      if (!previousIds.has(id)) arrived.push(id);
    });
    if (arrived.length > 0) addMany(arrived);
  }, [currentIds, dataVersionKey, addMany]);

  return ids;
}

// Ramps a numeric value from 0 to `target` over `durationMs` using rAF and an
// ease-out cubic curve. Returns `{ value, settled }` so callers can swap in a
// canonical formatted string once the animation finishes (this avoids the
// classic float-rounding desync between an animated display and an aria label).
//
// Behavior guarantees:
//  - First paint is 0 (not `target`) when an animation is about to run, so
//    there's no one-frame flash of the final number before the count-up starts.
//  - Target is snapshotted at the moment animation begins; live data updates
//    to `target` mid-animation do NOT restart the count-up.
//  - Non-finite targets coerce to 0; reduced-motion / disabled / SSR snap.
function useScoreCountUp({ target, durationMs, enabled, reduceMotion }) {
  const safeTarget = Number.isFinite(target) ? target : 0;
  const targetRef = useRef(safeTarget);
  useEffect(() => {
    targetRef.current = safeTarget;
  }, [safeTarget]);

  const shouldAnimate = enabled && !reduceMotion;
  const [value, setValue] = useState(() => (shouldAnimate ? 0 : safeTarget));
  const [settled, setSettled] = useState(() => !shouldAnimate);

  useEffect(() => {
    if (!enabled || reduceMotion) {
      setValue(targetRef.current);
      setSettled(true);
      return undefined;
    }
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      setValue(targetRef.current);
      setSettled(true);
      return undefined;
    }

    const startTarget = targetRef.current;
    let rafId = null;
    let startTimestamp = null;
    setValue(0);
    setSettled(false);

    const step = (timestamp) => {
      if (startTimestamp === null) startTimestamp = timestamp;
      const elapsed = timestamp - startTimestamp;
      const progress = Math.min(1, durationMs > 0 ? elapsed / durationMs : 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(startTarget * eased);
      if (progress < 1) {
        rafId = window.requestAnimationFrame(step);
      } else {
        setValue(startTarget);
        setSettled(true);
      }
    };

    rafId = window.requestAnimationFrame(step);

    return () => {
      if (rafId !== null && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(rafId);
      }
    };
    // Intentionally omitting `safeTarget` from deps: we snapshot it via
    // targetRef when each animation begins so mid-feature live updates can't
    // restart the count-up from zero.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, reduceMotion, durationMs]);

  return { value, settled };
}

// ─── Static visual fragments ────────────────────────────────────────────────
const NEW_HIGHLIGHT_KEYFRAMES = {
  '0%': {
    transform: 'translateY(-6px)',
    boxShadow: `0 0 0 2px ${goldRgba(0)}, 0 0 0 ${goldRgba(0)}`,
  },
  '30%': {
    boxShadow: `0 0 0 2px ${goldRgba(0.7)}, 0 0 36px ${goldRgba(0.55)}`,
  },
  '100%': {
    transform: 'translateY(0)',
    boxShadow: `0 0 0 2px ${goldRgba(0)}, 0 0 0 ${goldRgba(0)}`,
  },
};

const PULSE_KEYFRAMES = Object.freeze({
  '0%, 100%': { opacity: 1, transform: 'scale(1)' },
  '50%': { opacity: 0.4, transform: 'scale(0.85)' },
});

// Static gold "sheen" overlay that sits on top of the centered card. No
// animation — just a soft top-down highlight that suggests polished metal
// without producing the headache-inducing motion of a moving shimmer.
const FEATURE_STATIC_SHEEN_BG = (rgba) =>
  `linear-gradient(180deg, ${rgba(0.18)} 0%, ${rgba(0.06)} 32%, transparent 70%)`;

// Trophy icon does a single damped wiggle when the top-rank card lands centered.
const FEATURE_TROPHY_WIGGLE_KEYFRAMES = Object.freeze({
  '0%, 100%': { transform: 'rotate(0deg)' },
  '20%': { transform: 'rotate(-14deg)' },
  '40%': { transform: 'rotate(10deg)' },
  '60%': { transform: 'rotate(-6deg)' },
  '80%': { transform: 'rotate(3deg)' },
});

const REDUCED_MOTION_NO_ANIMATION_SX = Object.freeze({
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
});

const FOCUS_RING_SX = Object.freeze({
  outline: 'none',
  '&:focus-visible': { boxShadow: `0 0 0 2px ${ACCENT_BLUE}` },
});

const FOCUS_RING_INSET_SX = Object.freeze({
  outline: 'none',
  '&:focus-visible': { boxShadow: `inset 0 0 0 2px ${ACCENT_BLUE}` },
});

const SECTION_LABEL_BASE_SX = Object.freeze({
  color: MUTED_TEXT,
  fontWeight: 700,
  letterSpacing: 1.4,
  textTransform: 'uppercase',
  lineHeight: 1.2,
});

const RANK_BORDERS = Object.freeze({
  1: `linear-gradient(180deg, ${GOLD_DEEP} 0%, #C3A128 30%, #FEF89D 40%, ${GOLD} 80%)`,
  2: 'linear-gradient(180deg, #777777 0%, #C5C5C5 30%, #F0F0F0 40%, #767676 80%)',
  3: 'linear-gradient(180deg, #5B3619 0%, #90633A 30%, #F3D1A8 50%, #AD763D 70%, #7D4313 80%)',
});

const RANK_GLOW = Object.freeze({
  1: `0 0 24px ${goldRgba(0.25)}`,
  2: '0 0 18px rgba(192, 192, 192, 0.18)',
  3: '0 0 18px rgba(173, 118, 61, 0.18)',
});

const MONOGRAM_PALETTES = Object.freeze([
  { from: '#FF6B6B', to: '#C44569' },
  { from: '#4ECDC4', to: '#1E96A0' },
  { from: '#FFA726', to: '#E65100' },
  { from: '#7E57C2', to: '#4527A0' },
  { from: '#42A5F5', to: '#0D47A1' },
  { from: '#66BB6A', to: '#1B5E20' },
  { from: '#EC407A', to: '#880E4F' },
  { from: '#26C6DA', to: '#006064' },
]);

const STATE_BOX_SX = Object.freeze({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 200,
  padding: 4,
  background: ROW_BG,
  border: `1px solid ${ROW_BORDER}`,
  borderRadius: 1.5,
});

const BREADCRUMBS = Object.freeze([
  { title: 'Home', link: 'https://www.blackbox-vr.com' },
  { title: 'Community', link: '/' },
  { title: 'Demo Leaderboard' },
]);

// ─── Chart helpers ──────────────────────────────────────────────────────────
const BAR_GRADIENT_COORDS = Object.freeze({
  vertical: Object.freeze([0, 0, 0, 1]),
  horizontal: Object.freeze([0, 0, 1, 0]),
});

const BAR_SERIES_BORDER_RADIUS_VERTICAL = Object.freeze([6, 6, 0, 0]);
const BAR_SERIES_BORDER_RADIUS_HORIZONTAL = Object.freeze([0, 4, 4, 0]);

function buildBarGradient(orientation, fromColor, toColor) {
  const [x1, y1, x2, y2] = BAR_GRADIENT_COORDS[orientation] || BAR_GRADIENT_COORDS.vertical;
  return new echarts.graphic.LinearGradient(x1, y1, x2, y2, [
    { offset: 0, color: fromColor },
    { offset: 1, color: toColor },
  ]);
}

function buildBarItemStyle({ isTop, isNew, orientation }) {
  if (isNew) {
    return {
      color: buildBarGradient(orientation, GOLD_LIGHT, GOLD),
      borderColor: GOLD,
      borderWidth: 2,
      shadowColor: goldRgba(0.85),
      shadowBlur: 24,
    };
  }
  if (isTop) {
    return {
      color: buildBarGradient(orientation, GOLD_LIGHT, GOLD_DEEP),
      shadowColor: goldRgba(0.25),
      shadowBlur: 16,
    };
  }
  return {
    color: buildBarGradient(orientation, ACCENT_BLUE_LIGHT, ACCENT_BLUE),
  };
}

const CHART_AXIS_BASE = Object.freeze({
  axisLabel: Object.freeze({ color: CHART_AXIS_TEXT, fontWeight: 600, fontSize: 12 }),
  axisLine: Object.freeze({ lineStyle: Object.freeze({ color: CHART_AXIS_LINE }) }),
  axisTick: Object.freeze({ lineStyle: Object.freeze({ color: CHART_AXIS_LINE }) }),
});

const CHART_SPLIT_LINE = Object.freeze({
  lineStyle: Object.freeze({ color: CHART_AXIS_LINE, type: 'dashed', opacity: 0.4 }),
});

const CHART_BASE_OPTION = Object.freeze({
  backgroundColor: 'transparent',
  textStyle: Object.freeze({ fontFamily: 'inherit' }),
});

function chartAnimationOpts(reduceMotion) {
  if (reduceMotion) return Object.freeze({ animation: false, animationDuration: 0 });
  return Object.freeze({ animationDuration: 600, animationEasing: 'cubicOut' });
}

function buildChartTooltip(formatter) {
  return {
    trigger: 'axis',
    backgroundColor: CHART_TOOLTIP_BG,
    borderColor: ROW_BORDER,
    borderWidth: 1,
    padding: [8, 12],
    textStyle: { color: PRIMARY_TEXT, fontSize: 13 },
    axisPointer: { type: 'shadow' },
    formatter,
  };
}

function buildBarSeriesData(entries, metric, newIds, orientation) {
  return entries.map((entry) => ({
    value: getMetricNumber(entry, metric),
    itemStyle: buildBarItemStyle({
      isTop: safeRankNumber(entry?.rank) === TOP_RANK,
      isNew: isEntryNew(entry, newIds),
      orientation,
    }),
  }));
}

function buildVerticalBarOption({ entries, activeOption, newIds, reduceMotion }) {
  const { formatValue, formatAxis, title, metric } = resolveSortOption(activeOption);
  const slice = entries.slice(0, BAR_TOP_N_VERTICAL);
  const names = slice.map((entry) => getShortName(entry));
  const data = buildBarSeriesData(slice, metric, newIds, 'vertical');
  return {
    ...CHART_BASE_OPTION,
    grid: { left: 48, right: 24, top: 32, bottom: 64, containLabel: true },
    tooltip: buildChartTooltip((params) => {
      const point = params?.[0];
      if (!point) return '';
      const entry = slice[point.dataIndex];
      const rankLabel = escapeHtml(formatRankLabel(entry?.rank));
      const full = escapeHtml(getDisplayName(entry));
      return `<strong>${rankLabel} ${full}</strong><br/>${escapeHtml(formatValue(point.value))}`;
    }),
    xAxis: {
      type: 'category',
      data: names,
      ...CHART_AXIS_BASE,
      axisLabel: {
        ...CHART_AXIS_BASE.axisLabel,
        interval: 0,
        rotate: names.length > 8 ? 35 : 0,
        formatter: (value) =>
          typeof value === 'string' && value.length > 9 ? `${value.slice(0, 9)}…` : value,
      },
    },
    yAxis: {
      type: 'value',
      ...CHART_AXIS_BASE,
      splitLine: CHART_SPLIT_LINE,
      axisLabel: { ...CHART_AXIS_BASE.axisLabel, formatter: formatAxis },
    },
    series: [
      {
        type: 'bar',
        name: title,
        data,
        barMaxWidth: 48,
        itemStyle: { borderRadius: BAR_SERIES_BORDER_RADIUS_VERTICAL },
        label: {
          show: true,
          position: 'top',
          color: PRIMARY_TEXT,
          fontWeight: 700,
          formatter: (params) => formatValue(params.value),
        },
        emphasis: { focus: 'series' },
        ...chartAnimationOpts(reduceMotion),
      },
    ],
  };
}

function buildHorizontalBarOption({ entries, activeOption, newIds, reduceMotion }) {
  const { formatValue, formatAxis, title, metric } = resolveSortOption(activeOption);
  const slice = entries.slice(0, BAR_TOP_N_HORIZONTAL);
  const reversed = [...slice].reverse();
  const labels = reversed.map((entry) => `${formatRankLabel(entry?.rank)} ${getDisplayName(entry)}`.trim());
  const data = buildBarSeriesData(reversed, metric, newIds, 'horizontal');
  return {
    ...CHART_BASE_OPTION,
    grid: { left: 16, right: 64, top: 24, bottom: 32, containLabel: true },
    tooltip: buildChartTooltip((params) => {
      const point = params?.[0];
      if (!point) return '';
      const entry = reversed[point.dataIndex];
      const full = escapeHtml(getDisplayName(entry));
      const pageName = typeof entry?.pageName === 'string' ? entry.pageName.trim() : '';
      const eventLine = pageName
        ? `<br/><span style="color:${SOFT_TEXT}">${escapeHtml(pageName)}</span>`
        : '';
      return `<strong>${full}</strong>${eventLine}<br/>${escapeHtml(formatValue(point.value))}`;
    }),
    xAxis: {
      type: 'value',
      ...CHART_AXIS_BASE,
      splitLine: CHART_SPLIT_LINE,
      axisLabel: { ...CHART_AXIS_BASE.axisLabel, formatter: formatAxis },
    },
    yAxis: {
      type: 'category',
      data: labels,
      ...CHART_AXIS_BASE,
      axisLabel: {
        ...CHART_AXIS_BASE.axisLabel,
        fontSize: 13,
        formatter: (value) =>
          typeof value === 'string' && value.length > 24 ? `${value.slice(0, 24)}…` : value,
      },
    },
    series: [
      {
        type: 'bar',
        name: title,
        data,
        barMaxWidth: 24,
        itemStyle: { borderRadius: BAR_SERIES_BORDER_RADIUS_HORIZONTAL },
        label: {
          show: true,
          position: 'right',
          color: PRIMARY_TEXT,
          fontWeight: 700,
          formatter: (params) => formatValue(params.value),
        },
        emphasis: { focus: 'series' },
        ...chartAnimationOpts(reduceMotion),
      },
    ],
  };
}

function aggregateByEvent(entries, metric) {
  const groups = new Map();
  entries.forEach((entry) => {
    const key =
      typeof entry?.pageName === 'string' && entry.pageName.trim().length > 0
        ? entry.pageName.trim()
        : 'Other';
    const existing = groups.get(key) || { total: 0, count: 0, best: 0 };
    const value = getMetricNumber(entry, metric);
    existing.total += value;
    existing.count += 1;
    if (value > existing.best) existing.best = value;
    groups.set(key, existing);
  });
  return [...groups.entries()]
    .map(([name, agg]) => ({
      name,
      best: agg.best,
      average: agg.count > 0 ? agg.total / agg.count : 0,
      count: agg.count,
    }))
    .sort((a, b) => b.best - a.best)
    .slice(0, EVENT_SPREAD_MAX_EVENTS);
}

const EVENT_SERIES = Object.freeze({
  TOP: 'Top score',
  AVG: 'Average',
});

const EVENT_SERIES_DOT_COLOR = Object.freeze({
  [EVENT_SERIES.TOP]: GOLD,
  [EVENT_SERIES.AVG]: ACCENT_BLUE,
});

function buildEventSpreadOption({ entries, activeOption, reduceMotion }) {
  const { formatValue, formatAxis, metric } = resolveSortOption(activeOption);
  const rows = aggregateByEvent(entries, metric);
  const names = rows.map((row) => row.name);
  const bestData = rows.map((row) => ({
    value: row.best,
    itemStyle: buildBarItemStyle({ isTop: true, isNew: false, orientation: 'vertical' }),
  }));
  const avgData = rows.map((row) => ({
    value: row.average,
    itemStyle: buildBarItemStyle({ isTop: false, isNew: false, orientation: 'vertical' }),
  }));
  const rowsByName = new Map(rows.map((row) => [row.name, row]));
  return {
    ...CHART_BASE_OPTION,
    legend: {
      data: [EVENT_SERIES.TOP, EVENT_SERIES.AVG],
      textStyle: { color: CHART_AXIS_TEXT, fontWeight: 600 },
      top: 0,
    },
    grid: { left: 48, right: 32, top: 48, bottom: 80, containLabel: true },
    tooltip: buildChartTooltip((params) => {
      if (!Array.isArray(params) || params.length === 0) return '';
      const eventName = params[0].name;
      const row = rowsByName.get(eventName);
      const lines = params.map((p) => {
        const dot = EVENT_SERIES_DOT_COLOR[p.seriesName] || PRIMARY_TEXT;
        return `<span style="color:${dot}">●</span> ${escapeHtml(p.seriesName)}: <strong>${escapeHtml(formatValue(p.value))}</strong>`;
      });
      const countLine = row
        ? `<br/><span style="color:${SOFT_TEXT}">${row.count} demo${row.count === 1 ? '' : 's'}</span>`
        : '';
      return `<strong>${escapeHtml(eventName)}</strong><br/>${lines.join('<br/>')}${countLine}`;
    }),
    xAxis: {
      type: 'category',
      data: names,
      ...CHART_AXIS_BASE,
      axisLabel: {
        ...CHART_AXIS_BASE.axisLabel,
        interval: 0,
        rotate: 30,
        formatter: (value) =>
          typeof value === 'string' && value.length > 18 ? `${value.slice(0, 18)}…` : value,
      },
    },
    yAxis: {
      type: 'value',
      ...CHART_AXIS_BASE,
      splitLine: CHART_SPLIT_LINE,
      axisLabel: { ...CHART_AXIS_BASE.axisLabel, formatter: formatAxis },
    },
    series: [
      {
        type: 'bar',
        name: EVENT_SERIES.TOP,
        data: bestData,
        barMaxWidth: 28,
        itemStyle: { borderRadius: BAR_SERIES_BORDER_RADIUS_VERTICAL },
        ...chartAnimationOpts(reduceMotion),
      },
      {
        type: 'bar',
        name: EVENT_SERIES.AVG,
        data: avgData,
        barMaxWidth: 28,
        itemStyle: { borderRadius: BAR_SERIES_BORDER_RADIUS_VERTICAL },
        ...chartAnimationOpts(reduceMotion),
      },
    ],
  };
}

function buildDistributionBuckets(values, bucketCount) {
  if (values.length === 0 || bucketCount <= 0) return { buckets: [], min: 0, max: 0, width: 0 };
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) {
    return { buckets: [{ from: min, to: min, count: values.length }], min, max, width: 0 };
  }
  const width = (max - min) / bucketCount;
  const buckets = Array.from({ length: bucketCount }, (_, index) => ({
    from: min + index * width,
    to: min + (index + 1) * width,
    count: 0,
  }));
  values.forEach((value) => {
    const offset = value === max ? bucketCount - 1 : Math.floor((value - min) / width);
    const safeIndex = Math.max(0, Math.min(bucketCount - 1, offset));
    buckets[safeIndex].count += 1;
  });
  return { buckets, min, max, width };
}

function buildDistributionOption({ entries, activeOption, reduceMotion }) {
  const { formatAxis, metric } = resolveSortOption(activeOption);
  const values = entries.map((entry) => getMetricNumber(entry, metric));
  const { buckets } = buildDistributionBuckets(values, DISTRIBUTION_BUCKET_COUNT);
  const topValue = entries[0] ? getMetricNumber(entries[0], metric) : null;
  const labels = buckets.map((bucket) => {
    const lo = formatAxis(bucket.from);
    const hi = formatAxis(bucket.to);
    return lo === hi ? lo : `${lo}–${hi}`;
  });
  const data = buckets.map((bucket) => {
    const containsTop =
      topValue != null && topValue >= bucket.from && (topValue <= bucket.to || bucket.to === bucket.from);
    return {
      value: bucket.count,
      itemStyle: buildBarItemStyle({
        isTop: containsTop,
        isNew: false,
        orientation: 'vertical',
      }),
    };
  });
  return {
    ...CHART_BASE_OPTION,
    grid: { left: 48, right: 24, top: 32, bottom: 72, containLabel: true },
    tooltip: buildChartTooltip((params) => {
      const point = params?.[0];
      if (!point) return '';
      const bucket = buckets[point.dataIndex];
      // formatAxis returns numeric strings, so no user-controlled data
      // flows in here today — but we escape anyway so the invariant
      // "every tooltip interpolation goes through escapeHtml" is uniform.
      const range = bucket
        ? `${escapeHtml(formatAxis(bucket.from))}–${escapeHtml(formatAxis(bucket.to))}`
        : '';
      const plural = point.value === 1 ? 'demo' : 'demos';
      return `<strong>${range}</strong><br/>${escapeHtml(point.value)} ${plural}`;
    }),
    xAxis: {
      type: 'category',
      data: labels,
      ...CHART_AXIS_BASE,
      axisLabel: {
        ...CHART_AXIS_BASE.axisLabel,
        interval: 0,
        rotate: 35,
        fontSize: 11,
      },
    },
    yAxis: {
      type: 'value',
      ...CHART_AXIS_BASE,
      splitLine: CHART_SPLIT_LINE,
      minInterval: 1,
      axisLabel: { ...CHART_AXIS_BASE.axisLabel, formatter: (n) => formatCompactNumber(n, 0) },
    },
    series: [
      {
        type: 'bar',
        name: 'Demos',
        data,
        barMaxWidth: 56,
        itemStyle: { borderRadius: BAR_SERIES_BORDER_RADIUS_VERTICAL },
        ...chartAnimationOpts(reduceMotion),
      },
    ],
  };
}

const CHART_OPTION_BUILDERS = Object.freeze({
  [VIEW.BAR_VERTICAL]: buildVerticalBarOption,
  [VIEW.BAR_HORIZONTAL]: buildHorizontalBarOption,
  [VIEW.EVENT_SPREAD]: buildEventSpreadOption,
  [VIEW.DISTRIBUTION]: buildDistributionOption,
});

const CHART_META = Object.freeze({
  [VIEW.BAR_VERTICAL]: {
    title: (option) => `Top ${BAR_TOP_N_VERTICAL} by ${option.title}`,
    subtitle: 'Tap a bar for details',
    height: CHART_HEIGHT_PX,
  },
  [VIEW.BAR_HORIZONTAL]: {
    title: (option) => `Top ${BAR_TOP_N_HORIZONTAL} by ${option.title}`,
    subtitle: 'Leader at the top',
    height: CHART_HEIGHT_HORIZONTAL_PX,
  },
  [VIEW.EVENT_SPREAD]: {
    title: (option) => `${option.title} by Event`,
    subtitle: 'Top score vs. average per landing page',
    height: CHART_HEIGHT_PX,
  },
  [VIEW.DISTRIBUTION]: {
    title: (option) => `${option.title} Distribution`,
    subtitle: 'Demos grouped by score band',
    height: CHART_HEIGHT_PX,
  },
});

// ─── Fetcher ────────────────────────────────────────────────────────────────
async function fetchDemoLeaderboard([, calendarDays, sortBy, userTimezone, limit, exerciseFilter]) {
  const sourceExerciseId = exerciseFilterToSourceId(exerciseFilter);
  const payload = {
    action: DEMO_LEADERBOARD_ACTION,
    calendarDays,
    sortBy,
    userTimezone,
    limit,
  };
  if (sourceExerciseId !== null) {
    payload.exerciseId = sourceExerciseId;
  }
  const result = await callBackbonePostApi(payload);
  if (result?.status === SUCCESS_STATUS && result?.data) {
    return result.data;
  }
  throw new Error('Could not load the demo leaderboard.');
}

// ─── Sub-components ─────────────────────────────────────────────────────────
const SwitcherOption = ({ option, isSelected, onSelect }) => {
  const OptionIcon = option.Icon;
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      aria-label={option.title}
      onClick={onSelect}
      onKeyDown={(event) => handleActivationKeyDown(event, onSelect)}
      sx={{
        cursor: 'pointer',
        flex: 1,
        minWidth: 0,
        textAlign: 'center',
        padding: { xs: '10px 8px', sm: '12px 16px' },
        borderRadius: 1,
        background: isSelected ? ACCENT_BLUE : 'transparent',
        color: isSelected ? PRIMARY_TEXT : MUTED_TEXT,
        transition: 'background 0.15s ease, color 0.15s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        userSelect: 'none',
        '&:hover': {
          background: isSelected ? ACCENT_BLUE : ROW_BG,
          color: PRIMARY_TEXT,
        },
        ...FOCUS_RING_SX,
      }}
    >
      {OptionIcon && <OptionIcon sx={{ fontSize: 18 }} />}
      <Typography sx={{ fontWeight: 700, fontSize: { xs: 13, sm: 14 }, letterSpacing: 0.5 }}>
        {option.title}
      </Typography>
    </Box>
  );
};

const FiltersPanel = ({ isOpen, onToggle, summary, children }) => (
  <Box
    sx={{
      background: ROW_BG,
      border: `1px solid ${ROW_BORDER}`,
      borderRadius: 1.5,
      overflow: 'hidden',
    }}
  >
    <Box
      role="button"
      tabIndex={0}
      aria-expanded={isOpen}
      aria-controls={FILTERS_PANEL_DOM_ID}
      onClick={onToggle}
      onKeyDown={(event) => handleActivationKeyDown(event, onToggle)}
      sx={{
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        padding: { xs: '12px 14px', sm: '14px 18px' },
        transition: 'background 0.15s ease',
        '&:hover': { background: ROW_BG_HOVER },
        ...FOCUS_RING_INSET_SX,
      }}
    >
      <TuneIcon sx={{ color: MUTED_TEXT, fontSize: 18 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ ...SECTION_LABEL_BASE_SX, fontSize: 11 }}>Filters</Typography>
        {summary ? (
          <Typography
            sx={{
              color: PRIMARY_TEXT,
              fontSize: { xs: 13, sm: 14 },
              fontWeight: 600,
              mt: 0.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {summary}
          </Typography>
        ) : null}
      </Box>
      <ExpandMoreIcon
        aria-hidden="true"
        sx={{
          color: MUTED_TEXT,
          fontSize: 22,
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease',
        }}
      />
    </Box>
    <Collapse in={isOpen} timeout={FEATURE_FADE_MS} unmountOnExit>
      <Box
        id={FILTERS_PANEL_DOM_ID}
        sx={{
          padding: { xs: 2, sm: 2.5 },
          borderTop: `1px solid ${ROW_BORDER}`,
          background: PANEL_BG,
        }}
      >
        {children}
      </Box>
    </Collapse>
  </Box>
);

const Switcher = ({ label, Icon, options, selected, onChange, wrap = false }) => {
  const labelText = typeof label === 'string' ? label : '';
  const labelId = `switcher-label-${labelText.replace(/\s+/g, '-').toLowerCase() || 'unlabeled'}`;
  // With wrap=true the row can break across multiple lines and each option
  // gets a sensible minWidth so it never crushes to unreadable. Default
  // (wrap=false) keeps the original equal-flex single-row behavior used
  // by the existing 3–6 option pickers.
  const optionSx = wrap
    ? { flex: '1 1 auto', minWidth: { xs: 76, sm: 96 } }
    : { flex: 1, minWidth: 0 };
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
        {Icon && <Icon sx={{ color: MUTED_TEXT, fontSize: 18 }} />}
        <Typography id={labelId} sx={{ ...SECTION_LABEL_BASE_SX, fontSize: 12 }}>
          {labelText}
        </Typography>
      </Box>
      <Box
        role="group"
        aria-labelledby={labelId}
        sx={{
          display: 'flex',
          flexWrap: wrap ? 'wrap' : 'nowrap',
          background: PANEL_BG,
          borderRadius: 1.5,
          padding: '4px',
          gap: '4px',
          border: `1px solid ${ROW_BORDER}`,
        }}
      >
        {options.map((option) => (
          <Box key={option.id} sx={optionSx}>
            <SwitcherOption
              option={option}
              isSelected={option.id === selected}
              onSelect={() => onChange(option.id)}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const RANK_BADGE_DIMENSIONS = Object.freeze({
  xl: { box: { xs: 56, sm: 68, tl: 80 }, font: { xs: 16, sm: 20, tl: 22 } },
  lg: { box: { xs: 48, sm: 56, tl: 64 }, font: { xs: 14, sm: 16, tl: 18 } },
  md: { box: { xs: 40, sm: 44, tl: 48 }, font: { xs: 12, sm: 14 } },
  sm: { box: { xs: 36, sm: 40 }, font: { xs: 12, sm: 13 } },
});

const RankBadge = ({ rank, size = 'sm' }) => {
  const dimensions = RANK_BADGE_DIMENSIONS[size] || RANK_BADGE_DIMENSIONS.sm;
  const rankKey = safeRankNumber(rank);
  const borderBackground = (rankKey !== null && RANK_BORDERS[rankKey]) || PLAIN_BORDER;
  const boxShadowValue = (rankKey !== null && RANK_GLOW[rankKey]) || 'none';
  const label = formatRankLabel(rank);
  return (
    <Box
      sx={{
        width: dimensions.box,
        height: dimensions.box,
        background: borderBackground,
        borderRadius: '50%',
        padding: '2px',
        flexShrink: 0,
        boxShadow: boxShadowValue,
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          background: PANEL_BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: PRIMARY_TEXT,
          fontFamily: 'Futura',
          fontWeight: 800,
          fontSize: dimensions.font,
          lineHeight: 1,
        }}
      >
        {label}
      </Box>
    </Box>
  );
};

const MONOGRAM_DIMENSIONS = Object.freeze({
  xl: { xs: 104, sm: 136, tl: 168 },
  lg: { xs: 88, sm: 112, tl: 144 },
  md: { xs: 64, sm: 72, tl: 88 },
  sm: { xs: 52, sm: 60, tl: 68 },
});

const Monogram = ({ firstName, lastName, size = 'sm' }) => {
  const initial = getInitial(firstName);
  const palette = paletteForName(firstName, lastName);
  const dimensions = MONOGRAM_DIMENSIONS[size] || MONOGRAM_DIMENSIONS.sm;
  const InitialComponent =
    size === 'xl' || size === 'lg' ? Heading1 : size === 'md' ? Heading2 : Heading3;
  return (
    <Box
      sx={{
        width: dimensions,
        height: dimensions,
        flexShrink: 0,
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${palette.from} 0%, ${palette.to} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: PRIMARY_TEXT,
        textShadow: '0 2px 8px rgba(0,0,0,0.35)',
        boxShadow: 'inset 0 -8px 24px rgba(0,0,0,0.25)',
      }}
    >
      <InitialComponent>{initial}</InitialComponent>
    </Box>
  );
};

const SCORE_FONT_SIZE = Object.freeze({
  xl: { xs: 36, sm: 48, tl: 56 },
  lg: { xs: 28, sm: 36, tl: 44 },
  md: { xs: 22, sm: 26, tl: 32 },
  sm: { xs: 18, sm: 20 },
});

const SCORE_ICON_SIZE = Object.freeze({
  xl: { xs: 28, sm: 36, tl: 40 },
  lg: { xs: 22, sm: 28, tl: 32 },
  md: { xs: 18, sm: 22 },
  sm: { xs: 16, sm: 18 },
});

const ScoreDisplay = ({ formatted, Icon, size }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
    {Icon && (
      <Icon
        sx={{
          color: size === 'xl' || size === 'lg' ? GOLD : ACCENT_BLUE,
          fontSize: SCORE_ICON_SIZE[size] || SCORE_ICON_SIZE.sm,
        }}
      />
    )}
    <Typography
      sx={{
        fontFamily: 'Futura',
        fontWeight: 800,
        textTransform: 'uppercase',
        lineHeight: 1,
        fontSize: SCORE_FONT_SIZE[size] || SCORE_FONT_SIZE.sm,
      }}
    >
      {formatted}
    </Typography>
  </Box>
);

const PageChip = ({ pageName }) => {
  if (typeof pageName !== 'string') return null;
  const trimmed = pageName.trim();
  if (trimmed.length === 0) return null;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        maxWidth: '100%',
        background: accentBlueRgba(0.12),
        border: `1px solid ${accentBlueRgba(0.3)}`,
        color: ACCENT_BLUE_LIGHT,
        borderRadius: 999,
        padding: '3px 10px',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        mt: 0.75,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {trimmed}
    </Box>
  );
};

const NameBlock = ({ entry, size }) => {
  const name = getDisplayName(entry);
  let NameComponent;
  if (size === 'xl') NameComponent = Heading1;
  else if (size === 'lg') NameComponent = Heading2;
  else if (size === 'md') NameComponent = Heading3;
  else NameComponent = Heading4;
  return (
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <NameComponent sx={{ wordBreak: 'break-word', fontWeight: size === 'sm' ? 400 : undefined }}>
        {name}
      </NameComponent>
      <PageChip pageName={entry?.pageName} />
    </Box>
  );
};

const LeaderRow = ({
  entry,
  formattedValue,
  Icon,
  variant,
  isNew,
  disableMargin = false,
  isInFeatureOverlay = false,
  isFeatureCentered = false,
}) => {
  if (!isRecord(entry)) return null;
  const isTop = variant === 'top';
  const baseSize = rowSizeForVariant(variant);
  // When the row is rendered inside the feature overlay (centered or flying
  // back), every tier gets bumped up one notch: top -> xl, podium -> lg,
  // standard -> md. That way mid- and any-tier new demos are visibly bigger
  // than their normal-board appearance, and the overlay's CSS scale during
  // the fly shrinks the rendered size back down to match the natural slot.
  const size = isInFeatureOverlay ? SIZE_UP_ONE_TIER[baseSize] || baseSize : baseSize;

  const trophyWiggleSx = isTop && isFeatureCentered
    ? {
        animation: `${FEATURE_TROPHY_WIGGLE_ANIMATION_NAME} ${FEATURE_TROPHY_WIGGLE_MS}ms ease-in-out ${FEATURE_TROPHY_WIGGLE_DELAY_MS}ms 1 both`,
        [`@keyframes ${FEATURE_TROPHY_WIGGLE_ANIMATION_NAME}`]: FEATURE_TROPHY_WIGGLE_KEYFRAMES,
        transformOrigin: '50% 80%',
        display: 'inline-flex',
        ...REDUCED_MOTION_NO_ANIMATION_SX,
      }
    : null;

  const baseCardSx = isTop
    ? {
        padding: isInFeatureOverlay ? { xs: 3, sm: 4.5 } : { xs: 2.5, sm: 3.5 },
        borderRadius: 2,
        background: TOP_ROW_BG,
        border: `1px solid ${goldRgba(0.3)}`,
        boxShadow: `0 4px 32px ${goldRgba(0.08)}`,
      }
    : {
        padding: isInFeatureOverlay ? { xs: 2.5, sm: 3.5 } : { xs: 1.75, sm: 2.25 },
        borderRadius: 1.5,
        background: ROW_BG,
        border: `1px solid ${ROW_BORDER}`,
        transition: 'background 0.15s ease, border-color 0.15s ease',
        '&:hover': {
          background: ROW_BG_HOVER,
          borderColor: PLAIN_BORDER,
        },
      };

  const newEntrySx = isNew
    ? {
        animation: `${FEATURE_NEW_ROW_ANIMATION_NAME} ${NEW_ENTRY_HIGHLIGHT_MS}ms ease-out`,
        [`@keyframes ${FEATURE_NEW_ROW_ANIMATION_NAME}`]: NEW_HIGHLIGHT_KEYFRAMES,
        ...REDUCED_MOTION_NO_ANIMATION_SX,
      }
    : null;

  const marginTop = disableMargin ? 0 : (isTop ? 4 : 1.5);

  return (
    <Box sx={{ mt: marginTop, ...baseCardSx, ...newEntrySx }}>
      {isTop && (
        <Typography
          sx={{
            color: GOLD,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 2,
            textTransform: 'uppercase',
            mb: { xs: 1.5, sm: 2 },
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          <EmojiEventsIcon sx={{ fontSize: 14, ...(trophyWiggleSx || {}) }} />
          Top Demo
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: { xs: 1.5, sm: 2 },
          rowGap: { xs: 1.5, sm: 2 },
        }}
      >
        <RankBadge rank={entry?.rank} size={size} />
        <Monogram firstName={entry?.firstName} lastName={entry?.lastName} size={size} />
        <NameBlock entry={entry} size={size} />
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
          <ScoreDisplay formatted={formattedValue} Icon={Icon} size={size} />
        </Box>
      </Box>
    </Box>
  );
};

// ─── Feature animation: phase enum ──────────────────────────────────────────
const FEATURE_PHASE = Object.freeze({
  ENTER: 'enter',
  HOLD: 'hold',
  FLY: 'fly',
  SETTLE: 'settle',
  EXIT: 'exit',
  AFTER_EXIT: 'afterExit',
});

// ─── Feature overlay ────────────────────────────────────────────────────────
// Keyframes use scale(1) throughout so the visual center stays at the
// viewport center while the card rises from below. (With transform-origin
// 0 0 — which we use so the fly state anchors at slot.top/slot.left —
// a scale != 1 in the enter would shift the visual center off-axis.)
const FEATURE_OVERLAY_ENTER_KEYFRAMES = Object.freeze({
  '0%': { transform: 'translate(-50%, calc(-50% + 36px)) scale(1)', opacity: 0 },
  '100%': { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 },
});

const FEATURE_OVERLAY_CENTERED_SHADOW = `0 0 0 2px ${goldRgba(0.75)}, 0 0 64px ${goldRgba(0.45)}, 0 24px 64px rgba(0, 0, 0, 0.55)`;

const FeatureOverlay = ({
  entry,
  activeOption,
  phase,
  targetRect,
  variant: variantProp,
  reduceMotion = false,
}) => {
  const safeEntry = isRecord(entry) ? entry : null;
  const { Icon, formatValue, metric, title } = resolveSortOption(activeOption);
  const finalValue = safeEntry ? getMetricNumber(safeEntry, metric) : 0;

  const isCentered = phase === FEATURE_PHASE.ENTER || phase === FEATURE_PHASE.HOLD;
  const isExiting = phase === FEATURE_PHASE.EXIT || phase === FEATURE_PHASE.AFTER_EXIT;

  // Count up the displayed score while centered; the aria-label below always
  // uses the final value so screen readers aren't spammed with every frame.
  const { value: animatedValue, settled: countUpSettled } = useScoreCountUp({
    target: finalValue,
    durationMs: FEATURE_SCORE_COUNT_UP_MS,
    enabled: isCentered && safeEntry !== null,
    reduceMotion,
  });

  if (!safeEntry) return null;

  const formattedForAria = formatValue(finalValue);
  // Once the count-up settles, defer to the canonical formatted string so
  // metrics that emit decimals (e.g. weight in lbs) don't show a rounded
  // value that disagrees with the aria-label.
  const formattedForDisplay = countUpSettled
    ? formattedForAria
    : formatValue(Math.round(animatedValue));
  // Prefer the variant locked at dequeue so a mid-fly rank update can't flip
  // the visual treatment (e.g. gold "top" → silver "podium").
  const variant = variantProp || variantForRank(safeEntry?.rank);

  const hasValidRect =
    targetRect &&
    Number.isFinite(targetRect.top) &&
    Number.isFinite(targetRect.left) &&
    Number.isFinite(targetRect.width) &&
    targetRect.width > 0;

  // Outer width is constant throughout the reveal. During fly, the outer
  // itself scales (transform-origin top-left), which keeps the rendered
  // LeaderRow at its bumped 'xl/lg/md' tier and shrinks the entire visual
  // smoothly down to slot.width. Putting the scale on the outer (rather
  // than the inner card) also means the transformed visual bounding box
  // shrinks, which prevents the 880px-wide fixed element from causing a
  // horizontal scrollbar when it lands at slot.left on narrow viewports.
  //
  // We resolve the *same* width the browser will actually lay out from
  // `min(90vw, 880px)` so the scale denominator matches the CSS-resolved
  // box. documentElement.clientWidth excludes the vertical scrollbar
  // (which CSS `vw` also excludes on Chrome/Firefox), so the two stay in
  // sync better than `window.innerWidth` would.
  const overlayWidthPx = (() => {
    if (typeof document !== 'undefined' && document.documentElement) {
      const cw = document.documentElement.clientWidth;
      if (Number.isFinite(cw) && cw > 0) {
        return Math.min(cw * FEATURE_OVERLAY_VIEWPORT_RATIO, FEATURE_OVERLAY_MAX_WIDTH_PX);
      }
    }
    if (typeof window !== 'undefined' && Number.isFinite(window.innerWidth) && window.innerWidth > 0) {
      return Math.min(window.innerWidth * FEATURE_OVERLAY_VIEWPORT_RATIO, FEATURE_OVERLAY_MAX_WIDTH_PX);
    }
    return FEATURE_OVERLAY_MAX_WIDTH_PX;
  })();
  const outerScale =
    isCentered || !hasValidRect
      ? 1
      : Math.max(FEATURE_OVERLAY_MIN_OUTER_SCALE, targetRect.width / overlayWidthPx);

  let positionStyle;
  if (isCentered || !hasValidRect) {
    positionStyle = {
      top: '50%',
      left: '50%',
      // Keep transform function list identical to the fly state so CSS
      // interpolates translate + scale component-wise (smoother than the
      // matrix fallback when the lists differ).
      transform: `translate(-50%, -50%) scale(${outerScale})`,
      opacity: isExiting ? 0 : 1,
    };
  } else {
    positionStyle = {
      top: `${targetRect.top}px`,
      left: `${targetRect.left}px`,
      transform: `translate(0, 0) scale(${outerScale})`,
      opacity: isExiting ? 0 : 1,
    };
  }

  const transitionList =
    phase === FEATURE_PHASE.ENTER
      ? 'none'
      : [
          `top ${FEATURE_FLY_MS}ms ${FEATURE_FLY_EASING}`,
          `left ${FEATURE_FLY_MS}ms ${FEATURE_FLY_EASING}`,
          `transform ${FEATURE_FLY_MS}ms ${FEATURE_FLY_EASING}`,
          `opacity ${isExiting ? FEATURE_FADE_MS : FEATURE_FLY_MS}ms ease-out`,
        ].join(', ');

  const enterAnimationSx =
    phase === FEATURE_PHASE.ENTER
      ? {
          animation: `${FEATURE_OVERLAY_ENTER_ANIMATION_NAME} ${FEATURE_ENTER_MS}ms ${FEATURE_ENTER_EASING} both`,
          [`@keyframes ${FEATURE_OVERLAY_ENTER_ANIMATION_NAME}`]: FEATURE_OVERLAY_ENTER_KEYFRAMES,
          ...REDUCED_MOTION_NO_ANIMATION_SX,
        }
      : null;

  const rankNumber = safeRankNumber(safeEntry?.rank);
  const rankLabel = rankNumber !== null ? `rank ${rankNumber}` : '';
  const optionTitle = title ? title.toLowerCase() : '';
  const announcement = [
    'New demo',
    getDisplayName(safeEntry),
    rankLabel,
    optionTitle ? `${formattedForAria} ${optionTitle}` : formattedForAria,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-label={announcement}
      sx={{
        position: 'fixed',
        zIndex: FEATURE_OVERLAY_Z_INDEX,
        pointerEvents: 'none',
        width: FEATURE_OVERLAY_WIDTH_CSS,
        // Top-left origin: with translate(-50%, -50%) the centered state
        // still ends up viewport-centered at scale=1, and the fly state
        // anchors the scaled visual at slot.top/slot.left exactly.
        transformOrigin: '0 0',
        transition: transitionList,
        willChange: 'top, left, transform, opacity',
        ...positionStyle,
        ...enterAnimationSx,
      }}
    >
      {/* aria-hidden so the live region's aria-label isn't doubled up by the
          row's name, rank, and score being re-announced separately. */}
      <Box aria-hidden="true" sx={{ position: 'relative' }}>
        <Typography
          sx={{
            position: 'absolute',
            bottom: 'calc(100% + 12px)',
            left: 0,
            right: 0,
            textAlign: 'center',
            color: GOLD,
            fontSize: { xs: 12, sm: 13 },
            fontWeight: 800,
            letterSpacing: 3,
            textTransform: 'uppercase',
            textShadow: `0 0 24px ${goldRgba(0.5)}`,
            transition: `opacity ${FEATURE_FADE_MS}ms ease-out`,
            opacity: isCentered ? 1 : 0,
            pointerEvents: 'none',
          }}
        >
          ✨ New Demo
        </Typography>
        <Box
          sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: isCentered ? FEATURE_OVERLAY_CENTERED_SHADOW : `0 0 0 0 ${goldRgba(0)}`,
            transition: `box-shadow ${FEATURE_FLY_MS}ms ${FEATURE_FLY_EASING}`,
          }}
        >
          <LeaderRow
            entry={safeEntry}
            formattedValue={formattedForDisplay}
            Icon={Icon}
            variant={variant}
            isNew={false}
            disableMargin
            isInFeatureOverlay
            isFeatureCentered={isCentered}
          />
          {isCentered && (
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                background: FEATURE_STATIC_SHEEN_BG(goldRgba),
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

// ─── Leaderboard list / chart ───────────────────────────────────────────────
const LeaderboardList = ({
  entries,
  activeOption,
  newIds,
  featuredId,
  pendingFeatureId,
  queuedFeatureIds,
  recentlyRevealedIds,
  registerSlotRef,
}) => {
  const safeEntries = Array.isArray(entries) ? entries : [];
  const { Icon, formatValue, metric } = resolveSortOption(activeOption);
  const safeRevealed =
    recentlyRevealedIds && typeof recentlyRevealedIds.has === 'function'
      ? recentlyRevealedIds
      : null;
  const safeQueued =
    queuedFeatureIds && typeof queuedFeatureIds.has === 'function' ? queuedFeatureIds : null;
  // Defensively de-dupe React keys in case the backend ever ships duplicate
  // stable ids.
  const usedKeys = new Set();
  return (
    <Box>
      {safeEntries.map((entry, index) => {
        const value = getMetricNumber(entry, metric);
        const id = getEntryStableId(entry);
        const isFeatured =
          id !== null &&
          ((featuredId != null && id === featuredId) ||
            (pendingFeatureId != null && id === pendingFeatureId) ||
            (safeQueued !== null && safeQueued.has(id)));
        const slotRefCallback = isFeatured && registerSlotRef ? registerSlotRef(id) : undefined;
        const isJustRevealed =
          !isFeatured && safeRevealed !== null && id !== null && safeRevealed.has(id);
        let reactKey = getEntryReactKey(entry, index);
        if (usedKeys.has(reactKey)) reactKey = `${reactKey}__dup__${index}`;
        usedKeys.add(reactKey);
        return (
          <Box
            key={reactKey}
            ref={slotRefCallback}
            sx={{
              visibility: isFeatured ? 'hidden' : 'visible',
            }}
          >
            <LeaderRow
              entry={entry}
              formattedValue={formatValue(value)}
              Icon={Icon}
              variant={variantForRank(entry?.rank)}
              isNew={!isFeatured && (isJustRevealed || isEntryNew(entry, newIds))}
            />
          </Box>
        );
      })}
    </Box>
  );
};

// ─── TV / split-column layout ───────────────────────────────────────────────
// A two-column layout aimed at horizontal "TV" displays: left shows the
// rank-sorted leaderboard, right shows the most-recently-finished demos.
// The feature overlay is intentionally suppressed in this view (already
// handled by the `isListView` gate around the feature pipeline) because the
// Most Recent column delivers the same "see your score pop up" feedback as
// the centered overlay does in list view, without taking the screen over.
//
// Columns stack vertically on narrow viewports so the same view still
// renders gracefully on a phone or sidebar-constrained desktop.

function sortByRecency(entries) {
  if (!Array.isArray(entries)) return [];
  // Slice first so we don't mutate the SWR-cached array.
  return [...entries].sort((a, b) => {
    const at = Number(a?.finalizedAtMs);
    const bt = Number(b?.finalizedAtMs);
    const safeA = Number.isFinite(at) ? at : 0;
    const safeB = Number.isFinite(bt) ? bt : 0;
    return safeB - safeA;
  });
}

const TvColumn = ({ title, subtitle, Icon, entries, activeOption, newIds }) => {
  const { Icon: ScoreIcon, formatValue, metric } = resolveSortOption(activeOption);
  const safeEntries = Array.isArray(entries) ? entries : [];
  // De-dupe React keys defensively, same approach as LeaderboardList.
  const usedKeys = new Set();
  return (
    <Box
      sx={{
        background: PANEL_BG,
        border: `1px solid ${ROW_BORDER}`,
        borderRadius: 1.5,
        padding: { xs: 2, sm: 3 },
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'baseline',
          gap: 1.25,
          flexWrap: 'wrap',
        }}
      >
        <Heading3 sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {Icon ? <Icon sx={{ color: GOLD, fontSize: 22 }} /> : null}
          {title}
        </Heading3>
        {subtitle ? (
          <Typography sx={{ color: SOFT_TEXT, fontSize: 12 }}>{subtitle}</Typography>
        ) : null}
      </Box>
      {safeEntries.length === 0 ? (
        <Typography sx={{ color: MUTED_TEXT, py: 2 }}>No demos to show.</Typography>
      ) : (
        <Box>
          {safeEntries.map((entry, index) => {
            const value = getMetricNumber(entry, metric);
            let reactKey = getEntryReactKey(entry, index);
            if (usedKeys.has(reactKey)) reactKey = `${reactKey}__dup__${index}`;
            usedKeys.add(reactKey);
            return (
              <LeaderRow
                key={reactKey}
                entry={entry}
                formattedValue={formatValue(value)}
                Icon={ScoreIcon}
                variant={variantForRank(entry?.rank)}
                isNew={isEntryNew(entry, newIds)}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// Subtitle helpers keep the "no rows after filter" copy from reading as
// "Top 0 by rank" / "Newest 0 finishes", which looks like broken copy.
function topColumnSubtitle(count) {
  if (count === 0) return 'No qualifying demos yet';
  if (count === 1) return 'Top 1 by rank';
  return `Top ${count} by rank`;
}
function recentColumnSubtitle(count) {
  if (count === 0) return 'No qualifying demos yet';
  if (count === 1) return 'Newest finish';
  return `Newest ${count} finishes`;
}

const LeaderboardTvView = ({ entries, activeOption, newIds }) => {
  // `entries` is already filtered upstream via hasDisplayableData, so each
  // column only needs to slice and (for the recents) re-sort.
  const topEntries = useMemo(() => {
    if (!Array.isArray(entries)) return [];
    return entries.slice(0, TV_COLUMN_LIMIT);
  }, [entries]);
  const recentEntries = useMemo(() => {
    if (!Array.isArray(entries)) return [];
    return sortByRecency(entries).slice(0, TV_COLUMN_LIMIT);
  }, [entries]);
  const topCount = topEntries.length;
  const recentCount = recentEntries.length;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
        gap: { xs: 3, lg: 4 },
        alignItems: 'start',
      }}
    >
      <TvColumn
        title="Top Demos"
        subtitle={topColumnSubtitle(topCount)}
        Icon={EmojiEventsIcon}
        entries={topEntries}
        activeOption={activeOption}
        newIds={newIds}
      />
      <TvColumn
        title="Most Recent"
        subtitle={recentColumnSubtitle(recentCount)}
        Icon={AccessTimeIcon}
        entries={recentEntries}
        activeOption={activeOption}
        newIds={newIds}
      />
    </Box>
  );
};

const ChartCard = ({ title, subtitle, ariaLabel, option, height, optionKey }) => (
  <Box
    sx={{
      background: ROW_BG,
      border: `1px solid ${ROW_BORDER}`,
      borderRadius: 1.5,
      padding: { xs: 2, sm: 3 },
    }}
  >
    <Box
      sx={{
        mb: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        gap: 1,
      }}
    >
      <Heading3 sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <InsightsIcon sx={{ color: ACCENT_BLUE_LIGHT, fontSize: 22 }} />
        {title}
      </Heading3>
      {subtitle && <Typography sx={{ color: SOFT_TEXT, fontSize: 12 }}>{subtitle}</Typography>}
    </Box>
    <Box role="img" aria-label={ariaLabel} sx={{ height, width: '100%' }}>
      <ReactEcharts
        option={option}
        notMerge
        lazyUpdate
        style={{ height: '100%', width: '100%' }}
        opts={{ renderer: 'canvas' }}
        theme={undefined}
        key={optionKey}
      />
    </Box>
  </Box>
);

const LeaderboardChartView = ({ view, entries, activeOption, newIds, reduceMotion }) => {
  const builder = CHART_OPTION_BUILDERS[view];
  const meta = CHART_META[view];
  const option = useMemo(
    () => (builder ? builder({ entries, activeOption, newIds, reduceMotion }) : null),
    [builder, entries, activeOption, newIds, reduceMotion]
  );
  if (!builder || !meta) return null;
  const title = meta.title(activeOption);
  return (
    <ChartCard
      title={title}
      subtitle={meta.subtitle}
      ariaLabel={`${title} chart with ${Array.isArray(entries) ? entries.length : 0} demos`}
      option={option}
      height={meta.height}
      optionKey={`${view}-${activeOption.id}`}
    />
  );
};

const WindowStatusBar = ({ windowLabel, userTimezone, entryCount, isValidating, reduceMotion }) => {
  const summaryParts = [];
  if (windowLabel) summaryParts.push(windowLabel);
  if (userTimezone) summaryParts.push(userTimezone);
  summaryParts.push(`${entryCount} ${entryCount === 1 ? 'entry' : 'entries'}`);
  const pulseSx = reduceMotion
    ? null
    : {
        animation: `${FEATURE_PULSE_ANIMATION_NAME} 1.6s ease-in-out infinite`,
        [`@keyframes ${FEATURE_PULSE_ANIMATION_NAME}`]: PULSE_KEYFRAMES,
        ...REDUCED_MOTION_NO_ANIMATION_SX,
      };
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        mt: 3,
        flexWrap: 'wrap',
      }}
    >
      <Typography sx={{ color: SOFT_TEXT, fontSize: 13, letterSpacing: 0.3 }}>
        {summaryParts.join(' · ')}
      </Typography>
      <Box
        role="status"
        aria-live="polite"
        aria-label={
          isValidating
            ? 'Leaderboard refreshing'
            : `Live leaderboard, auto-refreshes every ${POLL_SECONDS_LABEL}`
        }
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          padding: '3px 10px',
          borderRadius: 999,
          background: LIVE_INDICATOR_BG,
          border: `1px solid ${LIVE_INDICATOR_BORDER}`,
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: LIVE_GREEN_DOT,
            ...pulseSx,
          }}
        />
        <Typography
          aria-hidden="true"
          sx={{
            color: LIVE_GREEN_TEXT,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
          }}
        >
          {isValidating ? 'Refreshing' : `Live · ${POLL_SECONDS_LABEL}`}
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Top-level container ────────────────────────────────────────────────────
const DemoLeaderboard = () => {
  const queryParameters = useQuery();
  const navigate = useNavigate();

  const calendarDays = clampInt(
    queryParameters.get('calendarDays'),
    MIN_CALENDAR_DAYS,
    MAX_CALENDAR_DAYS,
    DEFAULT_CALENDAR_DAYS
  );
  const sortBy = clampInt(
    queryParameters.get('sortBy'),
    SORT_BY.REP_COUNT,
    SORT_BY.HEAVIEST_REP_WEIGHT,
    DEFAULT_SORT_BY
  );
  const view = clampInt(queryParameters.get('view'), MIN_VIEW, MAX_VIEW, DEFAULT_VIEW);
  const limit = clampInt(queryParameters.get('limit'), MIN_LIMIT, MAX_LIMIT, DEFAULT_LIMIT);
  const exerciseFilter = normalizeExerciseFilter(queryParameters.get('exerciseId'));

  const userTimezone = useMemo(() => getBrowserTimezone(), []);

  const {
    data: response,
    error,
    isLoading,
    isValidating,
  } = useSWR(
    [SWR_KEY_PREFIX, calendarDays, sortBy, userTimezone, limit, exerciseFilter],
    fetchDemoLeaderboard,
    {
      refreshInterval: POLL_INTERVAL_MS,
      revalidateOnFocus: true,
      keepPreviousData: true,
      errorRetryCount: SWR_ERROR_RETRY_COUNT,
      errorRetryInterval: SWR_ERROR_RETRY_INTERVAL_MS,
    }
  );

  // The API receives the source numeric exerciseId for authoritative filtering
  // before ranking / limiting. Keep a defensive client-side pass so old
  // untagged demos remain visible in All only. Then drop any rows that would
  // render as placeholder em-dashes / "#NaN" so every downstream view (list,
  // TV, chart) renders only meaningful entries.
  const activeOption = getOptionForSortBy(sortBy);
  const { metric: activeMetric } = resolveSortOption(activeOption);
  const entries = useMemo(() => {
    const raw = Array.isArray(response?.entries) ? response.entries : [];
    const exerciseFiltered =
      exerciseFilter === EXERCISE_FILTER_ALL
        ? raw
        : raw.filter((entry) => normalizeExerciseId(entry?.exerciseId) === exerciseFilter);
    return exerciseFiltered.filter((entry) => hasDisplayableData(entry, activeMetric));
  }, [response, exerciseFilter, activeMetric]);

  // Keep exerciseFilter in the scope key so changing the filter resets
  // feature-arrival baselines and new-entry highlights.
  const swrKey = `${calendarDays}|${sortBy}|${limit}|${userTimezone}|${exerciseFilter}`;
  const newIds = useNewEntryIds(entries, NEW_ENTRY_HIGHLIGHT_MS, swrKey);

  const previousSwrKeyRef = useRef(swrKey);
  const isPendingSwrKeyChange = previousSwrKeyRef.current !== swrKey;
  const [isUserSwitching, setIsUserSwitching] = useState(false);

  useEffect(() => {
    if (previousSwrKeyRef.current === swrKey) return;
    previousSwrKeyRef.current = swrKey;
    setIsUserSwitching(true);
  }, [swrKey]);

  useEffect(() => {
    if (isUserSwitching && !isValidating) setIsUserSwitching(false);
  }, [isUserSwitching, isValidating]);

  const isListView = view === VIEW.LIST;
  const isTvView = view === VIEW.TV;

  const reduceMotion = usePrefersReducedMotion();

  const leaderboardSectionRef = useRef(null);
  const slotRefsRef = useRef(new Map());
  const slotRefCallbacksRef = useRef(new Map());
  const previousArrivalIdsRef = useRef(null);
  const featureResetSwrKeyRef = useRef(swrKey);

  const [featureQueue, setFeatureQueue] = useState([]);
  const [feature, setFeature] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { ids: recentlyRevealedIds, add: addRecentlyRevealedId } = useTimedIdSet({
    ttlMs: NEW_ENTRY_HIGHLIGHT_MS,
  });

  // Computed during render so we can synchronously hide the destination slot.
  const pendingFeatureId = computePendingFeatureId({
    entries,
    previousIds: previousArrivalIdsRef.current,
    enabled: isListView && !reduceMotion && !isUserSwitching,
  });

  // Bridges pendingFeatureId (pre-arrival-effect) and feature.id (post-dequeue)
  // so the destination slot stays hidden across every intermediate render.
  const queuedFeatureIds = useMemo(() => {
    const set = new Set();
    if (!Array.isArray(featureQueue)) return set;
    featureQueue.forEach((entry) => {
      const id = getEntryStableId(entry);
      if (id) set.add(id);
    });
    return set;
  }, [featureQueue]);

  useEffect(() => {
    if (featureResetSwrKeyRef.current === swrKey) return;
    featureResetSwrKeyRef.current = swrKey;
    previousArrivalIdsRef.current = null;
    setFeatureQueue([]);
    setFeature(null);
  }, [swrKey]);

  useEffect(() => {
    if (isListView && !reduceMotion) return;
    setFeatureQueue([]);
    setFeature(null);
  }, [isListView, reduceMotion]);

  useEffect(() => {
    if (isUserSwitching) return;
    if (!Array.isArray(entries)) return;

    // Reset baseline on empty payloads so a transient empty response between
    // two non-empty ones doesn't make every row look like a new arrival.
    if (entries.length === 0) {
      previousArrivalIdsRef.current = null;
      return;
    }

    const previousIds = previousArrivalIdsRef.current;
    const { currentIds, arrivals } = diffEntryIds(entries, previousIds);
    previousArrivalIdsRef.current = currentIds;

    if (previousIds === null) return;
    if (arrivals.length === 0) return;
    if (!isListView) return;
    if (reduceMotion) return;

    const topArrival = pickTopRankedArrival(arrivals);
    if (!topArrival) return;

    setFeatureQueue((prev) => [...prev, topArrival]);
  }, [entries, isListView, reduceMotion, isUserSwitching]);

  useEffect(() => {
    if (!isListView) return;
    if (feature) return;
    if (!Array.isArray(featureQueue) || featureQueue.length === 0) return;

    const [next, ...rest] = featureQueue;
    setFeatureQueue(rest);

    if (!isRecord(next)) return;
    const id = getEntryStableId(next);
    if (!id) return;

    // Prefer the live entry, but fall back to the queued snapshot so a poll
    // that briefly drops the row between enqueue and dequeue doesn't silently
    // swallow the showcase.
    const safeEntries = Array.isArray(entries) ? entries : [];
    const currentEntry = safeEntries.find((entry) => getEntryStableId(entry) === id) || next;

    // Cache the entry snapshot + locked variant + locked rank so a mid-fly
    // poll that removes the row or shifts its rank can't break the running
    // animation or visually drift the rank badge inside the gold treatment.
    setFeature({
      id,
      entry: currentEntry,
      variant: variantForRank(currentEntry?.rank),
      rank: safeRankNumber(currentEntry?.rank),
      phase: FEATURE_PHASE.ENTER,
      targetRect: null,
    });
  }, [isListView, feature, featureQueue, entries]);

  // Prefer the live entry but fall back to the cached snapshot if the row
  // vanished from `entries` mid-animation.
  const liveFeatureEntry = useMemo(() => {
    if (!feature) return null;
    const live = Array.isArray(entries)
      ? entries.find((entry) => getEntryStableId(entry) === feature.id)
      : null;
    const base = live || feature.entry || null;
    if (!base) return null;
    // Lock the rank to the snapshot so the badge inside the locked gold/
    // podium treatment doesn't visually drift if a poll bumps the rank
    // during HOLD. Falls back to the live rank if we never captured one.
    if (feature.rank !== null && feature.rank !== undefined) {
      return base.rank === feature.rank ? base : { ...base, rank: feature.rank };
    }
    return base;
  }, [feature, entries]);

  // Deps are narrowed to id/phase so a resize-driven targetRect update does
  // NOT cancel and restart the phase timer.
  useEffect(() => {
    if (!feature) return undefined;
    const featureId = feature.id;
    const phase = feature.phase;

    let phaseTimer;
    let scrollTimer;

    const advanceTo = (nextPhase, extraPatch) => {
      setFeature((prev) => {
        if (!prev || prev.id !== featureId) return prev;
        return { ...prev, phase: nextPhase, ...(extraPatch || {}) };
      });
    };

    if (phase === FEATURE_PHASE.ENTER) {
      scrollTimer = setTimeout(() => {
        const node = slotRefsRef.current.get(featureId);
        if (!node) return;
        smoothScrollPageToCenter(node, {
          durationMs: FEATURE_SMOOTH_SCROLL_MS,
          reduceMotion,
        });
      }, FEATURE_SCROLL_INTO_VIEW_DELAY_MS);
      phaseTimer = setTimeout(() => advanceTo(FEATURE_PHASE.HOLD), FEATURE_ENTER_MS);
    } else if (phase === FEATURE_PHASE.HOLD) {
      phaseTimer = setTimeout(() => {
        const node = slotRefsRef.current.get(featureId);
        const rect = measureRect(node);
        if (!rect) {
          advanceTo(FEATURE_PHASE.EXIT);
          return;
        }
        advanceTo(FEATURE_PHASE.FLY, { targetRect: rect });
      }, FEATURE_HOLD_MS);
    } else if (phase === FEATURE_PHASE.FLY) {
      phaseTimer = setTimeout(() => advanceTo(FEATURE_PHASE.SETTLE), FEATURE_FLY_MS);
    } else if (phase === FEATURE_PHASE.SETTLE) {
      phaseTimer = setTimeout(() => advanceTo(FEATURE_PHASE.EXIT), FEATURE_SETTLE_MS);
    } else if (phase === FEATURE_PHASE.EXIT) {
      phaseTimer = setTimeout(() => advanceTo(FEATURE_PHASE.AFTER_EXIT), FEATURE_FADE_MS);
    } else if (phase === FEATURE_PHASE.AFTER_EXIT) {
      // Intentionally do NOT scroll back to the top of the leaderboard — the
      // page stays where the user can see the row in its slot.
      phaseTimer = setTimeout(() => {
        addRecentlyRevealedId(featureId);
        setFeature((prev) => (prev && prev.id === featureId ? null : prev));
      }, FEATURE_REVEAL_HANDOFF_DELAY_MS);
    }

    return () => {
      if (phaseTimer) clearTimeout(phaseTimer);
      if (scrollTimer) clearTimeout(scrollTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feature?.id, feature?.phase, addRecentlyRevealedId, reduceMotion]);

  // Cancel any in-flight smooth scroll on unmount so a navigation can't
  // leave our rAF callback fighting the new page's scroll position.
  useEffect(() => cancelActiveSmoothScroll, []);

  // Re-measure targetRect on window resize during fly/settle so the CSS
  // transition glides to the correct destination. Split from the phase driver
  // so rect drift never restarts the phase timer.
  useEffect(() => {
    if (!feature) return undefined;
    const phase = feature.phase;
    if (phase !== FEATURE_PHASE.FLY && phase !== FEATURE_PHASE.SETTLE) return undefined;
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
      return undefined;
    }
    const featureId = feature.id;
    const onResize = () => {
      const node = slotRefsRef.current.get(featureId);
      const rect = measureRect(node);
      if (!rect) return;
      setFeature((prev) => {
        if (!prev || prev.id !== featureId) return prev;
        return { ...prev, targetRect: rect };
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feature?.id, feature?.phase]);

  const registerSlotRef = useCallback((id) => {
    if (id === null || id === undefined || id === '') return undefined;
    let key;
    try {
      key = String(id);
    } catch {
      return undefined;
    }
    const cache = slotRefCallbacksRef.current;
    if (!cache.has(key)) {
      cache.set(key, (node) => {
        const slots = slotRefsRef.current;
        if (node) slots.set(key, node);
        else slots.delete(key);
      });
    }
    return cache.get(key);
  }, []);

  const errorMessage = error
    ? error.response?.data?.message || error.message || 'Request failed.'
    : null;

  const exerciseFilterTitle =
    exerciseFilter === EXERCISE_FILTER_ALL
      ? null
      : EXERCISE_FILTER_OPTIONS.find((option) => option.id === exerciseFilter)?.title;
  const filtersSummary = [
    WINDOW_OPTIONS.find((option) => option.id === calendarDays)?.title,
    activeOption.title,
    VIEW_OPTIONS.find((option) => option.id === view)?.title,
    exerciseFilterTitle,
  ]
    .filter(Boolean)
    .join(' · ');

  const setQuery = (updates) => {
    const next = new URLSearchParams(queryParameters.toString());
    Object.entries(updates).forEach(([key, value]) => next.set(key, String(value)));
    navigate(`${ROUTE_PATH}?${next.toString()}`, { replace: true });
  };

  const hasResponse = response != null;
  if (isLoading && !hasResponse) return <Loading fullscreen />;

  const startLabel = formatDayKeyForDisplay(response?.startDayKey);
  const endLabel = formatDayKeyForDisplay(response?.endDayKey);
  const windowLabel = startLabel && endLabel ? `${startLabel} – ${endLabel}` : '';
  const isShowingPreviousResponse = hasResponse && (isPendingSwrKeyChange || isUserSwitching);
  const showRefreshDim = isShowingPreviousResponse;
  let containerMaxWidth;
  if (isListView) containerMaxWidth = LEADERBOARD_MAX_WIDTH_PX;
  else if (isTvView) containerMaxWidth = TV_MAX_WIDTH_PX;
  else containerMaxWidth = CHART_MAX_WIDTH_PX;
  const isFeatureVisuallyActive =
    !!feature && feature.phase !== FEATURE_PHASE.AFTER_EXIT;
  const activeFeatureId = isFeatureVisuallyActive ? feature.id : null;
  const showFeatureOverlay = isFeatureVisuallyActive && isListView && !!liveFeatureEntry;

  return (
    <Box>
      <MetaTags
        title="Black Box VR - Demo Leaderboard"
        description="Black Box VR - Demo Leaderboard"
      />
      <TopBanner
        title="Demo Leaderboard"
        subTitle={activeOption.title}
        breadcrumbs={BREADCRUMBS}
      />

      <Container sx={{ mt: { xs: 4, sm: 6 } }}>
        <FiltersPanel
          isOpen={filtersOpen}
          onToggle={() => setFiltersOpen((prev) => !prev)}
          summary={filtersSummary}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: { xs: 3, md: 4 },
            }}
          >
            <Switcher
              label="Time Period"
              Icon={CalendarMonthIcon}
              options={WINDOW_OPTIONS}
              selected={calendarDays}
              onChange={(id) => setQuery({ calendarDays: id })}
            />
            <Switcher
              label="Sort By"
              Icon={SortIcon}
              options={SORT_BY_OPTIONS}
              selected={sortBy}
              onChange={(id) => setQuery({ sortBy: id })}
            />
          </Box>
          <Box sx={{ mt: { xs: 3, md: 4 } }}>
            <Switcher
              label="View"
              Icon={InsightsIcon}
              options={VIEW_OPTIONS}
              selected={view}
              onChange={(id) => setQuery({ view: id })}
            />
          </Box>
          <Box sx={{ mt: { xs: 3, md: 4 } }}>
            <Switcher
              label="Exercise"
              Icon={FitnessCenterIcon}
              options={EXERCISE_FILTER_OPTIONS}
              selected={exerciseFilter}
              onChange={(id) => setQuery({ exerciseId: id })}
              wrap
            />
          </Box>
        </FiltersPanel>

        <WindowStatusBar
          windowLabel={windowLabel}
          userTimezone={userTimezone}
          entryCount={entries.length}
          isValidating={isValidating}
          reduceMotion={reduceMotion}
        />
      </Container>

      <Container sx={{ mt: 3, mb: 8, maxWidth: containerMaxWidth }}>
        <Box
          ref={leaderboardSectionRef}
          sx={{
            scrollMarginTop: { xs: 16, sm: 24 },
            opacity: showRefreshDim ? 0.6 : 1,
            transition: 'opacity 0.2s ease',
            pointerEvents: showRefreshDim ? 'none' : 'auto',
          }}
        >
          {errorMessage ? (
            <Box sx={STATE_BOX_SX}>
              <Typography color="error">{errorMessage}</Typography>
            </Box>
          ) : entries.length === 0 && isShowingPreviousResponse ? (
            <Box sx={STATE_BOX_SX}>
              <Typography sx={{ color: MUTED_TEXT }}>Updating leaderboard...</Typography>
            </Box>
          ) : entries.length === 0 ? (
            <Box sx={STATE_BOX_SX}>
              <Typography sx={{ color: MUTED_TEXT }}>No demos in this window.</Typography>
            </Box>
          ) : isListView ? (
            <LeaderboardList
              entries={entries}
              activeOption={activeOption}
              newIds={newIds}
              featuredId={activeFeatureId}
              pendingFeatureId={pendingFeatureId}
              queuedFeatureIds={queuedFeatureIds}
              recentlyRevealedIds={recentlyRevealedIds}
              registerSlotRef={registerSlotRef}
            />
          ) : isTvView ? (
            <LeaderboardTvView
              entries={entries}
              activeOption={activeOption}
              newIds={newIds}
            />
          ) : (
            <LeaderboardChartView
              view={view}
              entries={entries}
              activeOption={activeOption}
              newIds={newIds}
              reduceMotion={reduceMotion}
            />
          )}
        </Box>
      </Container>

      {showFeatureOverlay ? (
        <>
          <Box
            aria-hidden="true"
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: FEATURE_BACKDROP_Z_INDEX,
              pointerEvents: 'none',
              backgroundColor: `rgba(0, 0, 0, ${FEATURE_BACKDROP_ALPHA})`,
              opacity:
                feature.phase === FEATURE_PHASE.ENTER || feature.phase === FEATURE_PHASE.HOLD
                  ? 1
                  : 0,
              transition: `opacity ${FEATURE_BACKDROP_FADE_MS}ms ease-out`,
              willChange: 'opacity',
            }}
          />
          <FeatureOverlay
            entry={liveFeatureEntry}
            activeOption={activeOption}
            phase={feature.phase}
            targetRect={feature.targetRect}
            variant={feature.variant}
            reduceMotion={reduceMotion}
          />
        </>
      ) : null}

      <FooterMenu />
    </Box>
  );
};

export default DemoLeaderboard;
